// DEPENDENCIES
import React, { useMemo, useRef, useState, useReducer, useEffect } from "react";
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";

// MUI ICONS
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BadgeIcon from "@mui/icons-material/Badge";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TimelapseIcon from "@mui/icons-material/Timelapse";

// API
import { getAPICall, postAPICall, putAPICall } from "../../../config/apiCalls";

// CONSTANTS
import { notApplicable } from "../../Misc/Table/TanStackTableConstants";
import SpentHoursDialog from "./SpentHoursDialog";
import { NO_FEE_SCHEDULE_ID, findDefaultFeeSchedule, feeScheduleRateFor as rateFromFeeSchedule, unbudgetedServices, priceUnbudgetedService } from "./budgetPricing";

// ─── HELPERS ─────────────────────────────────────────────────────────────────

export const fmtCurrency = (value) => {
	const num = Number(value);
	if (!Number.isFinite(num)) return notApplicable;
	return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(num);
};

export const fmtHours = (value) => {
	const num = Number(value);
	if (!Number.isFinite(num)) return "0";
	return (Math.round(num * 100) / 100).toString();
};

const fmtPercent = (value) => {
	const num = Number(value);
	if (!Number.isFinite(num)) return notApplicable;
	return `${Math.round(num * 100) / 100}%`;
};

const fmtTimestamp = (value) => {
	if (!value) return notApplicable;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return notApplicable;
	return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const parseNum = (value) => {
	if (value === null || value === undefined || value === "") return null;
	const num = Number(value);
	return Number.isFinite(num) ? num : null;
};

// BQE formats Activity and Expense catalog item names as "{code}:{group}" and
// leaves a bare trailing colon when there's no group (e.g. "Parental Leave:")
// — cosmetic only, so the real name (with colon) is still what's stored/sent
// to BQE on save.
const displayItemName = (name) => (name && name.endsWith(":") ? name.slice(0, -1) : name);

// Money-input validation for Rate/Cost Rate fields — plain text (not type="number",
// which shows spinner arrows and doesn't play well with a "$" adornment), digits with
// at most one decimal point and up to 2 decimal digits.
const MONEY_REGEX = /^\d*\.?\d{0,2}$/;
const isValidMoneyInput = (value) => value === "" || MONEY_REGEX.test(value);
const moneyFieldProps = {
	InputProps: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
};

const activeOf = (list) => list.filter((r) => !r.deleted);

// Expense lines are listed alphabetically by item name (display order only —
// the working copy keeps BQE's order).
const byItemName = (getName) => (a, b) =>
	(displayItemName(getName(a)) || "").localeCompare(displayItemName(getName(b)) || "", undefined, { sensitivity: "base" });
const sortExpensesByItem = (rows) => [...rows].sort(byItemName((r) => r.itemName));

const cloneBudget = (b) => ({ service: b.service.map((r) => ({ ...r })), expense: b.expense.map((r) => ({ ...r })) });

// The server passes BQE's error body through as `error`, which can be a string,
// an object ({ message } / { Message }) or an array of validation errors. It's
// rendered as a React child, so it must always come out as a string — an object
// there crashes the whole page to a blank screen.
const saveErrorText = (err, fallback) => {
	const body = err?.response?.data;
	const toText = (e) => {
		if (e == null || e === "") return "";
		if (typeof e === "string") return e;
		if (Array.isArray(e)) return e.map(toText).filter(Boolean).join(" ");
		if (typeof e === "object") return toText(e.message ?? e.Message ?? e.error ?? e.errors ?? e.Errors) || JSON.stringify(e);
		return String(e);
	};
	return toText(body?.error) || toText(body?.message) || err?.message || fallback;
};

const serviceAmount = (row) => (Number(row.hours) || 0) * (Number(row.rate) || 0);
// BQE budget expense lines have no markup field — just a total costAmount and a
// chargeAmount; the markup % shown is the gap between them. It's read-only here:
// any expense line sent in an API save gets recalculated at 0% markup (see the
// server's expenseMarkupPercent), so markup can only be set in BQE. Lines with a
// markup can't be edited here (see markupById), new lines start at 0%, and every
// save is checked against what BQE actually kept (see findChargeMismatches).

// A line's charge (BQE's "Budget Amount", markup included). While its cost,
// units and markup are still what BQE gave us, that's BQE's own chargeAmount —
// rebuilding it from the rounded markup % could drift by a few dollars on large
// lines; once any of them is edited, it's recalculated.
const expensePriceBasis = (row) => `${Number(row.cost) || 0}|${Number(row.units) || 0}|${Number(row.markup) || 0}`;
const expenseCharge = (row) => (row.bqeCharge != null && row.bqeBasis === expensePriceBasis(row)
	? row.bqeCharge
	: (Number(row.cost) || 0) * (Number(row.units) || 0) * (1 + (Number(row.markup) || 0) / 100));
const roundCents = (value) => Math.round(value * 100) / 100;
// `cost` on a row is a per-unit rate; BQE stores the line's total cost.
const expenseAmountsFor = (row) => ({
	units: Number(row.units) || 0,
	costRate: Number(row.cost) || 0,
	costAmount: roundCents((Number(row.cost) || 0) * (Number(row.units) || 0)),
	chargeAmount: roundCents(expenseCharge(row)),
});
// Expense lines whose charge in BQE's save response isn't what it should be —
// BQE recalculated it, or the line vanished. Existing lines match by id; new
// ones (no id yet) by resource + item + cost.
const findChargeMismatches = (sentRows, savedBudget) => {
	const saved = savedBudget?.expenses || [];
	return sentRows.flatMap((row) => {
		const { costAmount, chargeAmount } = expenseAmountsFor(row);
		const match = row.bqeId
			? saved.find((l) => l.id === row.bqeId)
			: saved.find((l) => l.resourceId === row.resourceId && l.itemId === row.itemId && Math.abs((Number(l.costAmount) || 0) - costAmount) < 0.01);
		const label = displayItemName(row.itemName) || row.resourceName || "Expense line";
		if (!match) return row.bqeId ? [{ label, missing: true }] : [];
		if (Math.abs((Number(match.chargeAmount) || 0) - chargeAmount) < 0.01) return [];
		return [{ label, sent: chargeAmount, kept: Number(match.chargeAmount) || 0 }];
	});
};
// Markup % implied by a line's total cost and charge, to two decimals.
const markupFrom = (costAmount, chargeAmount) =>
	chargeAmount != null && costAmount > 0 ? Math.round(((chargeAmount / costAmount) - 1) * 10000) / 100 : 0;

// ─── REAL-DATA MAPPING ─────────────────────────────────────────────────────────
// BQE's synced line items don't always carry a resource id (older/legacy rows
// only have a resource name), so every resource is addressed by a "resourceKey"
// — `id:<bqeResourceId>` when we have one, otherwise `name:<name>` — reconciled
// below so the same person/vendor collapses to one key everywhere (budget lines,
// time entries) even when only some of those rows have an id. resourceKey is
// used purely for display/grouping (logged-hours matching); saving to BQE always
// needs the real resourceId, which is why the pickers below re-resolve it live.

export const buildResourceContext = (serviceItems, expenseItems, timeEntries, expenseEntries = []) => {
	const idByName = new Map();

	serviceItems.forEach((item) => {
		if (item.resource && item.resource_id && !idByName.has(item.resource)) idByName.set(item.resource, item.resource_id);
	});
	timeEntries.forEach((e) => {
		if (e.employee && e.resource_id && !idByName.has(e.employee)) idByName.set(e.employee, e.resource_id);
	});
	expenseEntries.forEach((e) => {
		if (e.resource && e.resource_id && !idByName.has(e.resource)) idByName.set(e.resource, e.resource_id);
	});

	const keyFor = (name, id) => {
		const resolvedId = id || (name ? idByName.get(name) : null);
		if (resolvedId) return `id:${resolvedId}`;
		return name ? `name:${name}` : "";
	};

	// All time logged counts against the budget, billable or not — the same
	// basis BQE uses for its own "budget spent".
	const hoursByKey = new Map();
	// $ side of hoursByKey, as BQE prices it: each entry at its own bill rate.
	// Entries synced before Bill_Rate was captured have no rate, so their hours
	// are kept apart (unratedHoursByKey) for the caller to price at a fallback.
	const ratedAmountByKey = new Map();
	const unratedHoursByKey = new Map();
	timeEntries.forEach((e) => {
		const key = keyFor(e.employee, e.resource_id);
		if (!key) return;
		const hours = Number(e.hours) || 0;
		hoursByKey.set(key, (hoursByKey.get(key) || 0) + hours);
		if (e.bill_rate != null) ratedAmountByKey.set(key, (ratedAmountByKey.get(key) || 0) + hours * (Number(e.bill_rate) || 0));
		else unratedHoursByKey.set(key, (unratedHoursByKey.get(key) || 0) + hours);
	});

	// The $ counterpart to hoursByKey — actual posted expense transactions
	// (BQE Expense Entries), not the budget's own expense line items.
	const expenseChargeByKey = new Map();
	expenseEntries.forEach((e) => {
		const key = keyFor(e.resource, e.resource_id);
		if (!key) return;
		expenseChargeByKey.set(key, (expenseChargeByKey.get(key) || 0) + (Number(e.charge_amount) || 0));
	});

	return { keyFor, hoursByKey, ratedAmountByKey, unratedHoursByKey, expenseChargeByKey };
};

// Dollar value of one resource's logged time: rated entries at their own bill
// rate, plus any not-yet-rated hours at `fallbackRate` (null → left unpriced).
export const spentAmountFor = (resourceContext, key, fallbackRate) => {
	const rated = resourceContext.ratedAmountByKey.get(key) || 0;
	const unrated = resourceContext.unratedHoursByKey.get(key) || 0;
	return rated + (fallbackRate != null ? unrated * (Number(fallbackRate) || 0) : 0);
};

// Dollar value of hours actually logged against budgeted resources — each
// entry at its own bill rate, with not-yet-rated hours at the resource's
// budgeted rate. Counted once per resource, using the rate off whichever of
// that resource's lines comes first as the fallback. `rows` only needs
// { resourceKey, rate } — shared with the At A Glance tab.
export const budgetedServicesSpent = (rows, resourceContext) => {
	const seenKeys = new Set();
	return rows.reduce((s, row) => {
		if (!row.resourceKey || seenKeys.has(row.resourceKey)) return s;
		seenKeys.add(row.resourceKey);
		return s + spentAmountFor(resourceContext, row.resourceKey, row.rate);
	}, 0);
};

// Splits each resource's logged time across their own budget lines, so a
// resource with two lines doesn't show the same entries on both. An entry goes
// to the line with its activity; one whose activity has no line of its own
// lands on that resource's first line, so per-resource totals are unchanged.
// Returns Map<row.id, { hours, amount, entries }>, amount priced like
// spentAmountFor (own bill rate, unrated hours at the line's rate).
// Time entries often carry only the activity code, without BQE's ":group"
// suffix (see matchActivity), so a full-name match falls back to the code.
const normalizeActivity = (name) => String(name || "").trim().toLowerCase();
const activityCode = (name) => normalizeActivity(name).split(":")[0].trim();
const lineForActivity = (candidates, activity) => {
	if (!activity) return null;
	return candidates.find((r) => normalizeActivity(r.activityName) === activity)
		|| candidates.find((r) => activityCode(r.activityName) === activityCode(activity))
		|| null;
};
export const lineSpendByRow = (rows, timeEntries, resourceContext) => {
	const rowsByKey = new Map();
	rows.forEach((row) => {
		if (!row.resourceKey) return;
		if (!rowsByKey.has(row.resourceKey)) rowsByKey.set(row.resourceKey, []);
		rowsByKey.get(row.resourceKey).push(row);
	});
	const byRow = new Map(rows.map((row) => [row.id, { hours: 0, ratedAmount: 0, unratedHours: 0, entries: [] }]));
	(timeEntries || []).forEach((e) => {
		const candidates = rowsByKey.get(resourceContext.keyFor(e.employee, e.resource_id));
		if (!candidates) return;
		const row = lineForActivity(candidates, normalizeActivity(e.activity_description)) || candidates[0];
		const spend = byRow.get(row.id);
		const hours = Number(e.hours) || 0;
		spend.hours += hours;
		if (e.bill_rate != null) spend.ratedAmount += hours * (Number(e.bill_rate) || 0);
		else spend.unratedHours += hours;
		spend.entries.push(e);
	});
	const rateById = new Map(rows.map((row) => [row.id, row.rate]));
	byRow.forEach((spend, id) => {
		const rate = rateById.get(id);
		spend.amount = spend.ratedAmount + (rate != null && rate !== "" ? spend.unratedHours * (Number(rate) || 0) : 0);
	});
	return byRow;
};

const seedServiceRows = (items, resourceContext) =>
	items.map((item, idx) => ({
		id: item.id || `svc-existing-${idx}`,
		bqeId: item.id || null,
		resourceKey: resourceContext.keyFor(item.resource, item.resource_id) || `unknown-${idx}`,
		resourceId: item.resource_id || null,
		resourceName: item.resource || "",
		resourceType: item.resource_type || "",
		activityId: null, // not carried by our cached read model — only resolved if re-picked
		activityName: item.item || "",
		description: item.itemDescription || "",
		hours: item.hours ?? "",
		rate: item.billRate ?? "",
		deleted: false,
	}));

// Real budget-expense line items don't carry a markup field, only the resulting
// charge amount — back it out from cost/units/charge for display purposes only.
const seedExpenseRows = (items, resourceContext) =>
	items.map((item, idx) => {
		const cost = parseNum(item.cost_rate) ?? 0;
		const units = parseNum(item.units_or_hours) ?? 0;
		const charge = parseNum(item.charge_amount);
		const markup = markupFrom(cost * units, charge);
		return {
			id: item.id || `exp-existing-${idx}`,
			bqeId: item.id || null,
			resourceKey: resourceContext.keyFor(item.resource, null) || `unknown-${idx}`,
			resourceId: null, // BQE_Expenses_Line_Items_2 syncs a resource name only; resolved if re-picked
			resourceName: item.resource || "",
			itemId: null, // the Expense catalog entry's id — not carried by our cached read model
			itemName: item.item || "",
			description: item.description || "",
			cost: item.cost_rate ?? "",
			units: item.units_or_hours ?? "",
			markup,
			bqeCharge: charge,
			bqeBasis: expensePriceBasis({ cost: item.cost_rate, units: item.units_or_hours, markup }),
			deleted: false,
		};
	});

// Maps a budget line straight off a live BQE GET/PUT/POST response — unlike the
// NocoDB-cached seed above, these carry the real resourceId/itemId directly, so
// editing them never needs the "omit unless resolved" workaround.
const mapLiveServiceRow = (item, resourceContext) => ({
	id: item.id,
	bqeId: item.id,
	resourceKey: resourceContext.keyFor(item.resource, item.resourceId) || `unknown-${item.id}`,
	resourceId: item.resourceId || null,
	resourceName: item.resource || "",
	resourceType: "", // not returned by the Budget object; resolved from the resource picker if reassigned
	resourceIsGroup: !!item.isResourceGroup, // e.g. "All Employees" — resourceId is then a Group id
	activityId: item.itemId || null,
	activityName: item.item || "",
	activityItemType: item.itemType ?? null, // 1 = activity, 139 = activity group (e.g. "All Activities")
	description: item.description || "",
	hours: item.hours ?? "",
	rate: item.billRate ?? "",
	deleted: false,
});

// Live BudgetExpense lines carry a *total* costAmount (not a per-unit rate) and
// no markup field — derive the per-unit rate and the markup % from cost/charge.
const mapLiveExpenseRow = (item, resourceContext) => {
	const costAmount = parseNum(item.costAmount) ?? 0;
	const units = parseNum(item.units) ?? 0;
	const costRate = units > 0 ? Math.round((costAmount / units) * 10000) / 10000 : costAmount;
	const bqeCharge = parseNum(item.chargeAmount);
	const markup = markupFrom(costAmount, bqeCharge);
	return {
		id: item.id,
		bqeId: item.id,
		resourceKey: resourceContext.keyFor(item.resource, item.resourceId) || `unknown-${item.id}`,
		resourceId: item.resourceId || null,
		resourceName: item.resource || "",
		resourceIsGroup: !!item.isResourceGroup,
		itemId: item.itemId || null,
		itemName: item.item || "",
		itemType: item.itemType ?? null, // 21 = expense item, 140 = expense group (e.g. "All Expenses")
		description: item.description || "",
		cost: costRate,
		units: item.units ?? "",
		markup,
		bqeCharge,
		bqeBasis: expensePriceBasis({ cost: costRate, units: item.units, markup }),
		deleted: false,
	};
};

const mapLiveBudgetToSeed = (liveBudget, resourceContext) => ({
	service: (liveBudget?.services || []).map((i) => mapLiveServiceRow(i, resourceContext)),
	expense: (liveBudget?.expenses || []).map((i) => mapLiveExpenseRow(i, resourceContext)),
});

// "No id" is spelled inconsistently across this file — `null` on rows seeded
// from BQE, `""` on a fresh/cleared draft field — so a strict `===` between an
// option's id and a draft's id can wrongly say "no match" even when both really
// mean "unset". Treat them as equal.
const idsMatch = (a, b) => (a || "") === (b || "");

// Prepends the row's current (possibly not-yet-resolved-to-a-real-id) value as a
// synthetic option so an untouched legacy line still shows its real BQE value in
// the picker, without forcing the user to re-pick before they can save.
const withCurrentOption = (catalog, currentId, currentName) => {
	if (!currentName) return catalog;
	if (currentId && catalog.some((o) => idsMatch(o.id, currentId))) return catalog;
	return [{ id: currentId || "", name: currentName }, ...catalog];
};

// Diff the working copy against the last-saved baseline, grouped for the
// change chip and the save-review dialog.
const diffBudget = (present, baseline) => {
	const diff = { added: [], edited: [], removed: [] };
	["service", "expense"].forEach((kind) => {
		const baseMap = new Map(baseline[kind].map((r) => [r.id, r]));
		present[kind].forEach((row) => {
			const base = baseMap.get(row.id);
			if (!base) {
				if (!row.deleted) diff.added.push({ kind, row });
				return;
			}
			if (row.deleted && !base.deleted) {
				diff.removed.push({ kind, row: base });
				return;
			}
			if (!row.deleted && JSON.stringify(row) !== JSON.stringify(base)) {
				diff.edited.push({ kind, row });
			}
		});
	});
	return diff;
};

const budgetReducer = (state, action) => {
	switch (action.type) {
		case "MUTATE": {
			const nextPresent = action.mutate(cloneBudget(state.present));
			return { ...state, present: nextPresent, past: [...state.past, cloneBudget(state.present)], future: [] };
		}
		case "UNDO": {
			if (!state.past.length) return state;
			const prev = state.past[state.past.length - 1];
			return { ...state, present: prev, past: state.past.slice(0, -1), future: [cloneBudget(state.present), ...state.future] };
		}
		case "REDO": {
			if (!state.future.length) return state;
			const next = state.future[0];
			return { ...state, present: next, past: [...state.past, cloneBudget(state.present)], future: state.future.slice(1) };
		}
		case "DISCARD": {
			return { ...state, present: cloneBudget(state.baseline), past: [...state.past, cloneBudget(state.present)], future: [] };
		}
		case "SAVE": {
			return { ...state, baseline: cloneBudget(state.present), past: [], future: [] };
		}
		// Replaces present+baseline wholesale and clears undo history — used to seed
		// the editor from a live BQE fetch (on tab open, or after a save/create
		// response), so the working copy always starts from BQE's canonical state
		// rather than carrying forward anything from before.
		case "HYDRATE": {
			return { present: cloneBudget(action.seed), baseline: cloneBudget(action.seed), past: [], future: [] };
		}
		default:
			return state;
	}
};

const RESOURCE_SELECT_LABEL = (r) => `${r.name}${r.type === "Vendor" ? " (Vendor)" : r.isGroup ? " (Group)" : ""}`;

// Activity/expense picker label — groups other than the self-explanatory
// "All Activities"/"All Expenses" get a suffix so e.g. the "Diagnose" group
// isn't mistaken for an individual activity.
const ITEM_SELECT_LABEL = (o) => {
	const name = displayItemName(o.name);
	return o.isGroup && !/^all\b/i.test(o.name) ? `${name} (Group)` : name;
};

const CHANGE_STATUS_META = {
	added: { label: "Added", color: "#2e7d32" },
	edited: { label: "Edited", color: "#1976d2" },
	removed: { label: "Removed", color: "#c62828" },
};

// Builds the { serviceChanges, expenseChanges } payload for PUT /api/bqe/budget/:id
// from a diff — see buildResourceContext's comment: fields are only included when
// we actually resolved a real BQE id, so the server knows to leave anything else
// (e.g. an untouched line's Activity) exactly as BQE already has it.
const buildChangePayload = (diff) => {
	const serviceChanges = [];
	const expenseChanges = [];

	diff.added.forEach(({ kind, row }) => {
		if (kind === "service") {
			serviceChanges.push({
				resourceId: row.resourceId,
				resource: row.resourceName,
				isResourceGroup: !!row.resourceIsGroup,
				itemId: row.activityId,
				item: row.activityName,
				itemType: row.activityItemType || undefined,
				description: row.description || "",
				hours: Number(row.hours) || 0,
				billRate: Number(row.rate) || 0,
			});
		} else {
			expenseChanges.push({
				resourceId: row.resourceId,
				resource: row.resourceName,
				isResourceGroup: !!row.resourceIsGroup,
				itemId: row.itemId,
				item: row.itemName,
				itemType: row.itemType || undefined,
				description: row.description || "",
				...expenseAmountsFor(row),
			});
		}
	});

	diff.edited.forEach(({ kind, row }) => {
		const change = { id: row.bqeId };
		if (row.resourceId) {
			change.resourceId = row.resourceId;
			change.resource = row.resourceName;
			// undefined on cached-snapshot rows, where we don't know — the server
			// then leaves BQE's existing resource-group flag untouched.
			if (row.resourceIsGroup !== undefined) change.isResourceGroup = !!row.resourceIsGroup;
		}
		change.description = row.description || "";
		if (kind === "service") {
			if (row.activityId) { change.itemId = row.activityId; change.item = row.activityName; change.itemType = row.activityItemType || undefined; }
			change.hours = Number(row.hours) || 0;
			change.billRate = Number(row.rate) || 0;
			serviceChanges.push(change);
		} else {
			if (row.itemId) { change.itemId = row.itemId; change.item = row.itemName; change.itemType = row.itemType || undefined; }
			Object.assign(change, expenseAmountsFor(row));
			expenseChanges.push(change);
		}
	});

	diff.removed.forEach(({ kind, row }) => {
		if (!row.bqeId) return; // a new-then-removed line never existed in BQE
		(kind === "service" ? serviceChanges : expenseChanges).push({ id: row.bqeId, deleted: true });
	});

	return { serviceChanges, expenseChanges };
};

// Builds the { serviceLines, expenseLines } payload for POST /api/bqe/budget —
// used when a phase has no budget yet, so every currently staged line is new.
const buildCreatePayload = (diff) => ({
	serviceLines: diff.added.filter((d) => d.kind === "service").map(({ row }) => ({
		resourceId: row.resourceId,
		resource: row.resourceName,
		isResourceGroup: !!row.resourceIsGroup,
		itemId: row.activityId,
		item: row.activityName,
		itemType: row.activityItemType || undefined,
		description: row.description || "",
		hours: Number(row.hours) || 0,
		billRate: Number(row.rate) || 0,
	})),
	expenseLines: diff.added.filter((d) => d.kind === "expense").map(({ row }) => ({
		resourceId: row.resourceId,
		resource: row.resourceName,
		isResourceGroup: !!row.resourceIsGroup,
		itemId: row.itemId,
		item: row.itemName,
		itemType: row.itemType || undefined,
		description: row.description || "",
		...expenseAmountsFor(row),
	})),
});

// ─── LOADING / ERROR / EMPTY STATES ────────────────────────────────────────────

const renderLoadingNotice = () => (
	<div className="projectViewerLoadingNotice">
		<div className="projectViewerLoadingNoticeIconWrap">
			<TimelapseIcon fontSize="inherit" />
		</div>
		<div className="projectViewerLoadingNoticeContent">
			<div className="projectViewerLoadingNoticeTitle">Loading</div>
			<div className="projectViewerLoadingNoticeMessage">Loading budget data...</div>
		</div>
		<div className="projectViewerLoadingNoticeBars">
			<div className="projectViewerLoadingNoticeBar projectViewerShimmer" />
			<div className="projectViewerLoadingNoticeBar projectViewerShimmer short" />
		</div>
	</div>
);

// A horizontal stacked progress bar — segments fill left to right against
// `target` as the 100%-width goal (budgeted hours, or contract amount). If
// the segments add up to more than target, everything scales down to fit
// the track and a marker line shows where target actually sits, so an
// over-budget/over-contract situation is visible instead of just clipped.
export const ProgressStackBar = ({ title, target, segments, formatValue }) => {
	const total = segments.reduce((s, seg) => s + (seg.value > 0 ? seg.value : 0), 0);
	const basis = Math.max(target, total, 1);
	const overTarget = target > 0 && total > target;
	const markerPct = target > 0 ? Math.min((target / basis) * 100, 100) : null;
	const visibleSegments = segments.filter((seg) => seg.value > 0);

	const segmentTooltip = (seg) => (
		<div>
			<div style={{ fontWeight: 600 }}>{seg.label}</div>
			<div>{formatValue(seg.value)}</div>
		</div>
	);

	return (
		<div className="projectViewerBudgetProgressBar">
			<div className="projectViewerBudgetProgressHeader">
				{title ? <span>{title}</span> : <span />}
				<span style={{ color: overTarget ? "#c62828" : undefined, fontWeight: 600 }}>
					{formatValue(total)} of {formatValue(target)}
					{overTarget ? ` (+${formatValue(total - target)} over)` : ""}
				</span>
			</div>
			<div className="projectViewerBudgetProgressTrack">
				{visibleSegments.map((seg) => (
					<Tooltip key={seg.key} title={segmentTooltip(seg)} arrow>
						<div
							className="projectViewerBudgetProgressSegment"
							style={{ width: `${(seg.value / basis) * 100}%`, background: seg.color }}
						/>
					</Tooltip>
				))}
				{markerPct != null && (
					<Tooltip title={`Target: ${formatValue(target)}`} arrow>
						<div className="projectViewerBudgetProgressMarker" style={{ left: `${markerPct}%` }} />
					</Tooltip>
				)}
			</div>
			<div className="projectViewerBudgetProgressLegend">
				{visibleSegments.map((seg) => (
					<Tooltip key={seg.key} title={segmentTooltip(seg)} arrow>
						<div className="projectViewerBudgetProgressLegendItem">
							<span className="projectViewerBudgetProgressLegendDot" style={{ background: seg.color }} />
							{seg.label}: <strong>{formatValue(seg.value)}</strong>
						</div>
					</Tooltip>
				))}
			</div>
		</div>
	);
};

// ─── COMPONENT ───────────────────────────────────────────────────────────────

const BudgetEditorTab = ({ bqeBudgetUrl, budgetsSection, timeEntriesSection, expenseEntriesSection, atAGlanceSection, hasBqeId, phaseBqeId, loading, error, phaseRecordId, phaseName }) => {
	if (loading) {
		return <div className="projectViewerTabPanel">{renderLoadingNotice()}</div>;
	}
	if (error) {
		return <div className="projectViewerTabPanel"><div className="projectViewerEmptyStateMessage">{error}</div></div>;
	}
	if (!hasBqeId) {
		return (
			<div className="projectViewerTabPanel">
				<div className="projectViewerMissingInfoNotice">
					<InfoOutlinedIcon fontSize="inherit" />
					<div>Budget information is incomplete for this phase.</div>
				</div>
			</div>
		);
	}
	return (
		<BudgetEditorPanel
			bqeBudgetUrl={bqeBudgetUrl}
			budgetsSection={budgetsSection || {}}
			timeEntriesSection={timeEntriesSection || {}}
			expenseEntriesSection={expenseEntriesSection || {}}
			atAGlanceSection={atAGlanceSection || {}}
			phaseBqeId={phaseBqeId}
			phaseRecordId={phaseRecordId}
			phaseName={phaseName}
		/>
	);
};

// Mounted only once real section data is available — its reducer seeds from
// props exactly once (lazy useReducer init), so a background refresh of the
// underlying sections won't clobber in-progress edits.
const BudgetEditorPanel = ({ bqeBudgetUrl, budgetsSection, timeEntriesSection, expenseEntriesSection, atAGlanceSection, phaseBqeId, phaseRecordId, phaseName }) => {
	const { instance, accounts } = useMsal();
	// bqe_budget_id starts from the (hourly-synced) props but becomes local state
	// once known, so creating a budget in this session flips the UI over to the
	// save (rather than create) path without needing a remount.
	const [bqeBudgetId, setBqeBudgetId] = useState(budgetsSection.bqe_budget_id || null);
	const serviceLineItems = budgetsSection.service_line_items || [];
	const expenseLineItems = budgetsSection.expense_line_items || [];
	const timeEntries = timeEntriesSection.entries || [];
	const expenseEntries = expenseEntriesSection.entries || [];

	const resourceContext = useMemo(
		() => buildResourceContext(serviceLineItems, expenseLineItems, timeEntries, expenseEntries),
		[] // eslint-disable-line react-hooks/exhaustive-deps
	);

	// Line items start empty and are hydrated below from a live BQE fetch (not
	// the NocoDB cache) as soon as the tab opens, so an interactive edit/save
	// session is never working from up-to-an-hour-stale data.
	const [state, dispatch] = useReducer(budgetReducer, { present: { service: [], expense: [] }, baseline: { service: [], expense: [] }, past: [], future: [] });
	const [lineItemsLoading, setLineItemsLoading] = useState(!!bqeBudgetId);
	const [lineItemsStale, setLineItemsStale] = useState(false);
	const [subTab, setSubTab] = useState("service");
	// Independent per kind so a service edit and an expense edit/add can be in
	// progress at the same time — the user only ever sees one sub-tab at once,
	// but switching tabs shouldn't discard whichever one isn't currently visible.
	const [editingByKind, setEditingByKind] = useState({ service: null, expense: null }); // { id } | null
	const [draftByKind, setDraftByKind] = useState({ service: null, expense: null });
	const [confirmDelete, setConfirmDelete] = useState(null); // { kind, id, label }
	const [saveReviewOpen, setSaveReviewOpen] = useState(false);
	// Spent Hours breakdown dialog — { key } for one employee, { key: null } for everyone.
	const [spentHoursDialog, setSpentHoursDialog] = useState(null);
	const [toast, setToast] = useState(null); // { text, canUndo }
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [chargeMismatches, setChargeMismatches] = useState([]); // see findChargeMismatches
	const [feeScheduleId, setFeeScheduleId] = useState(""); // create-budget only
	const [selectedFeeSchedule, setSelectedFeeSchedule] = useState(null); // { services: [{resourceId,itemId,rate}], expenses: [...] }
	const [feeScheduleLoading, setFeeScheduleLoading] = useState(false);
	const nextIdRef = useRef(1);

	// Fetches the chosen fee schedule's actual rate table as soon as it's picked,
	// so new lines can be priced from it instead of just Activity/Expense catalog
	// defaults — that's the whole point of requiring one before adding lines.
	useEffect(() => {
		if (!feeScheduleId) { setSelectedFeeSchedule(null); setFeeScheduleLoading(false); return; }
		let cancelled = false;
		setFeeScheduleLoading(true);
		getAPICall(instance, accounts[0], `/api/bqe/feeschedule/${feeScheduleId}`)
			.then((res) => { if (!cancelled) setSelectedFeeSchedule(res.data); })
			.catch(() => { if (!cancelled) setSelectedFeeSchedule(null); })
			.finally(() => { if (!cancelled) setFeeScheduleLoading(false); });
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [feeScheduleId]);

	// Rate from the selected fee schedule — the primary rate source once one's chosen.
	const feeScheduleRateFor = (kind, resourceId, itemId) => rateFromFeeSchedule(selectedFeeSchedule, kind, resourceId, itemId);

	// Live BQE catalogs — required to pick a real resource / activity / expense
	// item for a new or reassigned line. Fetched once; the editor still works for
	// hours/rate/cost tweaks on existing lines while these are loading.
	const [activityCatalog, setActivityCatalog] = useState([]);
	const [expenseCatalog, setExpenseCatalog] = useState([]);
	const [resourceCatalog, setResourceCatalog] = useState([]);
	const [feeScheduleCatalog, setFeeScheduleCatalog] = useState([]);
	const [catalogsLoading, setCatalogsLoading] = useState(true);
	const [catalogsError, setCatalogsError] = useState("");

	useEffect(() => {
		let cancelled = false;
		// allSettled — rather than all-or-nothing — so one catalog failing (e.g. a
		// BQE-side query error specific to that entity) doesn't also blank out the
		// other two, which would otherwise still have loaded fine.
		Promise.allSettled([
			getAPICall(instance, accounts[0], "/api/bqe/activity/all"),
			getAPICall(instance, accounts[0], "/api/bqe/expense/all"),
			getAPICall(instance, accounts[0], "/api/bqe/resource/all"),
			getAPICall(instance, accounts[0], "/api/bqe/feeschedule/all"),
		]).then(([activityRes, expenseRes, resourceRes, feeScheduleRes]) => {
			if (cancelled) return;
			const failedLabels = [];
			if (activityRes.status === "fulfilled") setActivityCatalog(activityRes.value.data || []);
			else failedLabels.push("activities");
			if (expenseRes.status === "fulfilled") setExpenseCatalog(expenseRes.value.data || []);
			else failedLabels.push("expense items");
			if (resourceRes.status === "fulfilled") setResourceCatalog(resourceRes.value.data || []);
			else failedLabels.push("resources");
			if (feeScheduleRes.status === "fulfilled") {
				const feeSchedules = feeScheduleRes.value.data || [];
				setFeeScheduleCatalog(feeSchedules);
				// Default to the claims sheet (claims phases) or the "2026" sheet when
				// creating a new budget — still just a starting pick, not locked in;
				// the Autocomplete stays editable.
				const defaultSheet = findDefaultFeeSchedule(feeSchedules, phaseName);
				if (defaultSheet) setFeeScheduleId((current) => current || defaultSheet.id);
			} else failedLabels.push("fee schedules");
			if (failedLabels.length) {
				setCatalogsError(`Failed to load the BQE ${failedLabels.join("/")} list. ${failedLabels.length > 1 ? "Those pickers aren't" : "That picker isn't"} available right now.`);
			}
			setCatalogsLoading(false);
		});

		if (!bqeBudgetId) {
			// Nothing to fetch — this phase has no budget yet, so the editor starts
			// from an empty (create-budget) state.
			dispatch({ type: "HYDRATE", seed: { service: [], expense: [] } });
			setLineItemsLoading(false);
			return () => { cancelled = true; };
		}

		getAPICall(instance, accounts[0], `/api/bqe/budget/${bqeBudgetId}`)
			.then((res) => {
				if (cancelled) return;
				dispatch({ type: "HYDRATE", seed: mapLiveBudgetToSeed(res.data, resourceContext) });
				// Pick up the fee schedule this budget already uses in BQE, so rate
				// resolution for Not-In-Budget previews and any new lines added to it
				// works the same way it does for a brand-new budget. BQE represents
				// "none set" as an all-zero guid, not null.
				if (res.data.feeScheduleId && res.data.feeScheduleId !== NO_FEE_SCHEDULE_ID) {
					setFeeScheduleId(res.data.feeScheduleId);
				}
			})
			.catch((err) => {
				if (cancelled) return;
				if (err?.response?.status === 404) {
					// The budget id we had (from the hourly-synced cache) doesn't resolve
					// in BQE anymore — not a transient failure, there's really no budget
					// to edit. Drop back to the create-budget flow rather than showing
					// stale lines as if they were still safe to save against.
					setBqeBudgetId(null);
					dispatch({ type: "HYDRATE", seed: { service: [], expense: [] } });
					return;
				}
				// Any other failure (network, auth, BQE 5xx) — fall back to the
				// hourly-synced snapshot rather than blocking the tab. Edits still save
				// normally; only an untouched line's Activity/Expense item is left as-is
				// server-side rather than resent (see buildChangePayload), and figures
				// may be stale until the next sync.
				setLineItemsStale(true);
				dispatch({
					type: "HYDRATE",
					seed: { service: seedServiceRows(serviceLineItems, resourceContext), expense: seedExpenseRows(expenseLineItems, resourceContext) },
				});
			})
			.finally(() => { if (!cancelled) setLineItemsLoading(false); });

		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!toast) return;
		const t = setTimeout(() => setToast(null), 6000);
		return () => clearTimeout(t);
	}, [toast]);

	const { present, baseline } = state;
	const diff = useMemo(() => diffBudget(present, baseline), [present, baseline]);
	const changeCount = diff.added.length + diff.edited.length + diff.removed.length;
	const noChanges = changeCount === 0;
	// Looked up per row in the save-review preview, so the full working budget can
	// be shown with each row's change (if any) highlighted inline, rather than a
	// separate list of just what changed.
	const changeStatusById = useMemo(() => {
		const map = new Map();
		diff.added.forEach(({ kind, row }) => map.set(`${kind}:${row.id}`, "added"));
		diff.edited.forEach(({ kind, row }) => map.set(`${kind}:${row.id}`, "edited"));
		diff.removed.forEach(({ kind, row }) => map.set(`${kind}:${row.id}`, "removed"));
		return map;
	}, [diff]);

	const mutate = (fn) => dispatch({ type: "MUTATE", mutate: fn });

	const cancelEdit = (kind) => {
		setEditingByKind((prev) => ({ ...prev, [kind]: null }));
		setDraftByKind((prev) => ({ ...prev, [kind]: null }));
	};

	const setDraftForKind = (kind, updater) => {
		setDraftByKind((prev) => ({ ...prev, [kind]: typeof updater === "function" ? updater(prev[kind]) : updater }));
	};

	const startAddService = (prefill) => {
		if (editingByKind.service) return; // a service row is already being edited/added — don't clobber that draft
		if (needsFeeSchedule) return; // rates depend on the fee schedule — pick one first
		const id = `new-s-${nextIdRef.current++}`;
		setEditingByKind((prev) => ({ ...prev, service: { id } }));
		setDraftForKind("service", {
			id,
			bqeId: null,
			resourceKey: "",
			resourceId: "",
			resourceName: "",
			resourceType: "",
			resourceIsGroup: false,
			activityId: "",
			activityName: "",
			activityItemType: null,
			description: "",
			hours: "",
			rate: "",
			...prefill,
		});
		setSubTab("service");
	};

	const startAddExpense = (prefill) => {
		if (editingByKind.expense) return; // an expense row is already being edited/added — don't clobber that draft
		if (needsFeeSchedule) return; // rates depend on the fee schedule — pick one first
		const id = `new-x-${nextIdRef.current++}`;
		setEditingByKind((prev) => ({ ...prev, expense: { id } }));
		setDraftForKind("expense", { id, bqeId: null, resourceKey: "", resourceId: "", resourceName: "", resourceIsGroup: false, itemId: "", itemName: "", itemType: null, description: "", cost: "", units: 1, markup: 0, ...prefill });
		setSubTab("expense");
	};

	const startEdit = (kind, row) => {
		if (editingByKind[kind]) return; // a row of this kind is already being edited/added — don't clobber that draft
		setEditingByKind((prev) => ({ ...prev, [kind]: { id: row.id } }));
		setDraftForKind(kind, { ...row });
	};

	const commitEdit = (kind) => {
		const currentEditing = editingByKind[kind];
		const currentDraft = draftByKind[kind];
		if (!currentEditing || !currentDraft) return;
		const row = { ...currentDraft, deleted: false };
		mutate((b) => {
			const list = b[kind];
			const idx = list.findIndex((r) => r.id === row.id);
			if (idx >= 0) list[idx] = row;
			else list.push(row);
			return b;
		});
		cancelEdit(kind);
	};

	const deleteRow = (kind, id) => {
		const inBaseline = baseline[kind].some((r) => r.id === id);
		mutate((b) => {
			const list = b[kind];
			if (!inBaseline) {
				b[kind] = list.filter((r) => r.id !== id);
				return b;
			}
			const idx = list.findIndex((r) => r.id === id);
			if (idx >= 0) list[idx] = { ...list[idx], deleted: true };
			return b;
		});
		setConfirmDelete(null);
	};

	// A staged-only line (added this session, never saved) has nothing to warn
	// about — deleting it doesn't touch BQE, so skip the confirm dialog and just
	// remove it. Only a line that's actually in BQE already gets the "this will
	// be removed from BQE when you save" confirmation.
	const requestDelete = (kind, row) => {
		const inBaseline = baseline[kind].some((r) => r.id === row.id);
		if (!inBaseline) {
			deleteRow(kind, row.id);
			return;
		}
		setConfirmDelete({ kind, id: row.id, label: rowLabel(kind, row) });
	};

	const restoreRow = (kind, id) => {
		mutate((b) => {
			const idx = b[kind].findIndex((r) => r.id === id);
			if (idx >= 0) b[kind][idx] = { ...b[kind][idx], deleted: false };
			return b;
		});
	};

	const onDiscard = () => {
		dispatch({ type: "DISCARD" });
		cancelEdit("service");
		cancelEdit("expense");
		setToast({ text: "Unsaved budget changes discarded.", canUndo: true });
	};

	const onConfirmSave = async () => {
		if (saving) return;
		setSaving(true);
		setSaveError("");
		setChargeMismatches([]);
		const sentExpenseRows = activeOf(present.expense);
		try {
			let updatedBudget;
			if (bqeBudgetId) {
				const { serviceChanges, expenseChanges } = buildChangePayload(diff);
				const res = await putAPICall(instance, accounts[0], `/api/bqe/budget/${bqeBudgetId}`, { serviceChanges, expenseChanges, phaseRecordId });
				updatedBudget = res.data;
				setToast({ text: "Saved to BQE.", canUndo: false });
			} else {
				if (!phaseBqeId) throw new Error("This phase has no BQE project id — can't create a budget.");
				const { serviceLines, expenseLines } = buildCreatePayload(diff);
				const res = await postAPICall(instance, accounts[0], "/api/bqe/budget", { phaseBqeId, feeScheduleId: feeScheduleId || null, serviceLines, expenseLines, phaseRecordId });
				updatedBudget = res.data;
				setBqeBudgetId(updatedBudget.id);
				setToast({ text: "Budget created in BQE.", canUndo: false });
			}
			// Reseed from BQE's own response (not just our optimistic local guess) so
			// the working copy exactly matches what BQE now has — no wait for the
			// hourly sync, and no drift if BQE normalized anything on write.
			dispatch({ type: "HYDRATE", seed: mapLiveBudgetToSeed(updatedBudget, resourceContext) });
			setChargeMismatches(findChargeMismatches(sentExpenseRows, updatedBudget));
			setLineItemsStale(false);
			setSaveReviewOpen(false);
		} catch (err) {
			setSaveError(saveErrorText(err, "Failed to save budget changes to BQE."));
		} finally {
			setSaving(false);
		}
	};

	// ── Derived totals (from the interactive working copy) ────────────────────
	const serviceActive = activeOf(present.service);
	const expenseActive = activeOf(present.expense);
	const serviceHoursTotal = serviceActive.reduce((s, r) => s + (Number(r.hours) || 0), 0);
	const serviceFeeTotal = serviceActive.reduce((s, r) => s + serviceAmount(r), 0);
	const expenseCostTotal = expenseActive.reduce((s, r) => s + (Number(r.cost) || 0) * (Number(r.units) || 0), 0);
	const expenseChargeTotal = expenseActive.reduce((s, r) => s + expenseCharge(r), 0);
	const grandTotal = serviceFeeTotal + expenseChargeTotal;
	const budgetedResourceKeys = new Set(serviceActive.map((r) => r.resourceKey));
	const budgetedExpenseResourceKeys = new Set(expenseActive.map((r) => r.resourceKey));
	// Hours logged against resources that already have a budget line — used to
	// flag any resource that's over its own budgeted hours.
	const serviceLoggedTotal = [...budgetedResourceKeys].reduce((s, key) => s + (resourceContext.hoursByKey.get(key) || 0), 0);
	// All hours logged against this phase, budgeted or not — the "full" figure
	// shown alongside serviceLoggedTotal so both are visible at once.
	const allLoggedHoursTotal = [...resourceContext.hoursByKey.values()].reduce((s, h) => s + (h || 0), 0);
	// The $ counterpart to serviceLoggedTotal.
	const serviceSpentAmountTotal = budgetedServicesSpent(serviceActive, resourceContext);
	// Per-line share of that logged time (split by activity within a resource).
	const serviceLineSpend = lineSpendByRow(serviceActive, timeEntries, resourceContext);

	const unbudgeted = useMemo(() => {
		// A new (not-yet-applied) service line being drafted right now also hides
		// its resource here — otherwise it'd show in both places until Apply is
		// clicked, which reads as a duplicate/confusing. Cancelling clears this
		// automatically (nothing else needs to "put it back").
		const isDraftingNewService = editingByKind.service && !present.service.some((r) => r.id === editingByKind.service.id);
		const draftedResourceKey = isDraftingNewService ? draftByKind.service?.resourceKey : null;
		const excludeKeys = new Set(budgetedResourceKeys);
		if (draftedResourceKey) excludeKeys.add(draftedResourceKey);
		return unbudgetedServices(serviceLineItems, timeEntries, resourceContext, excludeKeys);
	}, [present.service, editingByKind.service, draftByKind.service]); // eslint-disable-line react-hooks/exhaustive-deps

	// Same idea as `unbudgeted` above, but for actual posted expense
	// transactions (BQE Expense Entries) against a resource with no expense
	// budget line — the expense-side analog of an unbudgeted service.
	const unbudgetedExpenses = useMemo(() => {
		const resourceByKey = new Map();
		expenseLineItems.forEach((i) => { if (i.resource) resourceByKey.set(resourceContext.keyFor(i.resource, i.resource_id), { name: i.resource, type: i.resource_type || "" }); });
		expenseEntries.forEach((e) => { if (e.resource) resourceByKey.set(resourceContext.keyFor(e.resource, e.resource_id), resourceByKey.get(resourceContext.keyFor(e.resource, e.resource_id)) || { name: e.resource, type: "" }); });

		// The expense item most often billed under, per resource — shown so
		// adding an unbudgeted resource isn't picking an item blind, and used to
		// pre-select a real Expense catalog entry if one matches.
		const itemTallyByKey = new Map();
		expenseEntries.filter((e) => e.item).forEach((e) => {
			const key = resourceContext.keyFor(e.resource, e.resource_id);
			if (!key) return;
			const tally = itemTallyByKey.get(key) || new Map();
			tally.set(e.item, (tally.get(e.item) || 0) + 1);
			itemTallyByKey.set(key, tally);
		});
		const topItemByKey = new Map();
		itemTallyByKey.forEach((tally, key) => {
			let best = "";
			let bestCount = 0;
			tally.forEach((count, text) => { if (count > bestCount) { best = text; bestCount = count; } });
			if (best) topItemByKey.set(key, best);
		});

		const unitsByKey = new Map();
		expenseEntries.forEach((e) => {
			const key = resourceContext.keyFor(e.resource, e.resource_id);
			if (!key) return;
			unitsByKey.set(key, (unitsByKey.get(key) || 0) + (Number(e.units) || 0));
		});

		const isDraftingNewExpense = editingByKind.expense && !present.expense.some((r) => r.id === editingByKind.expense.id);
		const draftedResourceKey = isDraftingNewExpense ? draftByKind.expense?.resourceKey : null;

		return [...resourceContext.expenseChargeByKey.entries()]
			.filter(([key, charge]) => charge > 0 && !budgetedExpenseResourceKeys.has(key) && key !== draftedResourceKey)
			.map(([key, charge]) => ({
				key,
				charge,
				units: unitsByKey.get(key) || 0,
				resource: resourceByKey.get(key) || null,
				billedItem: topItemByKey.get(key) || "",
			}))
			.sort((a, b) => b.charge - a.charge);
	}, [present.expense, editingByKind.expense, draftByKind.expense]); // eslint-disable-line react-hooks/exhaustive-deps

	// The real BQE description for an activity — falls back to its (colon-
	// trimmed) name when it has no distinct description set, same convention
	// used for existing real budget lines (itemDescription = Description || Item).
	const activityDescriptionFor = (activityMatch, fallbackName) =>
		activityMatch?.description || displayItemName(activityMatch?.name || fallbackName) || "";

	// A resource-specific fallback bill rate, pulled from any other budgeted
	// service line already using this resource in this phase. BQE's Activity
	// catalog usually leaves billRate blank (real rates are resource-specific,
	// not activity-specific), so the matched Activity's own rate is often null.
	const resourceRateFallback = (resourceKeyToMatch) => {
		const match = serviceLineItems.find(
			(i) => i.billRate != null && resourceContext.keyFor(i.resource, i.resource_id) === resourceKeyToMatch,
		);
		return match ? match.billRate : null;
	};

	// Expense-side counterparts to matchActivity/activityDescriptionFor/
	// resourceRateFallback above, used for the expense "Not In Budget" rows.
	const matchExpenseItem = (billedItem) => {
		if (!billedItem) return null;
		const target = billedItem.trim().toLowerCase();
		const codeOf = (name) => (name || "").split(":")[0].trim().toLowerCase();
		// Only real expense items — never auto-match an expense entry to a group.
		const items = expenseCatalog.filter((a) => !a.isGroup);
		return items.find((a) => (a.name || "").trim().toLowerCase() === target)
			|| items.find((a) => codeOf(a.name) === target)
			|| null;
	};

	const expenseCostRateFallback = (resourceKeyToMatch) => {
		const match = expenseLineItems.find(
			(i) => i.cost_rate != null && resourceContext.keyFor(i.resource, null) === resourceKeyToMatch,
		);
		return match ? match.cost_rate : null;
	};

	// Resolved once per not-in-budget resource so the preview shown in each
	// row, the "+" button's prefill, and the totals row below all agree on
	// the same rate/amount instead of recomputing it three separate times.
	// Time/expense entries are always logged by a real person or vendor, so
	// resource groups (e.g. "All Employees") are never a match for them.
	const individualResources = resourceCatalog.filter((r) => !r.isGroup);
	const pricingSources = { feeSchedule: selectedFeeSchedule, activityCatalog, resourceCatalog, serviceLineItems, keyFor: resourceContext.keyFor };
	const unbudgetedWithPreview = unbudgeted.map((u) => priceUnbudgetedService(u, pricingSources));
	const unbudgetedAmountTotal = unbudgetedWithPreview.reduce((s, u) => s + (u.spentAmount || 0), 0);

	// Expense-side counterpart. Unlike services, the charge here is already a
	// real, posted transaction amount — no rate/hours estimation needed, only
	// a cost-rate preview for the "+" button's prefill.
	const unbudgetedExpensesWithPreview = unbudgetedExpenses.map((u) => {
		const keyResourceId = u.key.startsWith("id:") ? u.key.slice(3) : null;
		const catalogMatch = (keyResourceId && individualResources.find((r) => r.id === keyResourceId))
			|| individualResources.find((r) => r.name === u.resource?.name);
		const itemMatch = matchExpenseItem(u.billedItem);
		const resolvedResourceId = catalogMatch?.id || keyResourceId || "";
		const previewCostRate = feeScheduleRateFor("expense", resolvedResourceId, itemMatch?.id) ?? itemMatch?.costRate ?? expenseCostRateFallback(u.key) ?? null;
		return { ...u, catalogMatch, itemMatch, resolvedResourceId, previewCostRate };
	});
	const unbudgetedExpenseAmountTotal = unbudgetedExpensesWithPreview.reduce((s, u) => s + (u.charge || 0), 0);
	// Services + expenses not-in-budget combined — used for phase-wide dollar
	// comparisons (contract amount, "Budgeted Total" in the info box) where
	// the figure isn't specific to one line-item kind.
	const combinedUnbudgetedAmountTotal = unbudgetedAmountTotal + unbudgetedExpenseAmountTotal;

	// "Full" totals fold in not-in-budget lines (both kinds) at their real/
	// preview amount, so the contract-amount comparison reflects everything
	// logged or billed, not just what's been formally budgeted so far.
	const fullServiceFeeTotal = serviceFeeTotal + unbudgetedAmountTotal;
	const fullExpenseChargeTotal = expenseChargeTotal + unbudgetedExpenseAmountTotal;
	const fullGrandTotal = fullServiceFeeTotal + fullExpenseChargeTotal;

	// Real spend to date, regardless of what's in the budget — the same basis
	// as BQE's "budget spent": every hour logged, billable or not, at its own
	// bill rate, plus every posted expense. Time
	// entries carry no $ amount of their own, so services are valued at these
	// rates; expenses are their actual posted charge amounts. Priced from its
	// own list (not `unbudgeted`) so a line being drafted doesn't drop out.
	const spentUnbudgeted = unbudgetedServices(serviceLineItems, timeEntries, resourceContext, budgetedResourceKeys)
		.map((u) => priceUnbudgetedService(u, pricingSources));
	const spentUnbudgetedServicesTotal = spentUnbudgeted.reduce((s, u) => s + (u.spentAmount || 0), 0);
	const spentServicesTotal = serviceSpentAmountTotal + spentUnbudgetedServicesTotal;
	const spentExpensesTotal = expenseEntries.reduce((s, e) => s + (Number(e.charge_amount) || 0), 0);
	const spentToDateTotal = spentServicesTotal + spentExpensesTotal;
	// Not-in-budget hours with no rate on the fee schedule (or anywhere else)
	// can't be priced — call them out rather than silently undercount.
	const unpricedUnbudgetedHours = spentUnbudgeted.reduce((s, u) => s + (u.unpricedHours || 0), 0);

	const contractAmount = parseNum(atAGlanceSection.contract_amount);
	// Invoicing figures for the Finances progress bar — from BQE's hourly
	// audit sync, same as the other invoice_summary fields used elsewhere.
	const invoicedAmount = atAGlanceSection?.invoice_summary?.total_invoice_amount ?? 0;
	const invoicedUnpaidAmount = atAGlanceSection?.invoice_summary?.total_balance ?? 0;
	const overdueAmount = atAGlanceSection?.invoice_summary?.overdue_balance ?? 0;
	// overdue_balance is already counted in total_balance, so split it back out
	// into its own segment instead of double-counting it.
	const unpaidNotOverdueAmount = Math.max(invoicedUnpaidAmount - overdueAmount, 0);
	const paidAmount = Math.max(invoicedAmount - invoicedUnpaidAmount, 0);
	const budgetedNotInvoicedAmount = Math.max(grandTotal - invoicedAmount, 0);
	// Whether the budget actually being saved exceeds contract — used by the
	// save-review dialog, which only reflects what's about to be written to BQE.
	const budgetedOverContract = contractAmount != null && grandTotal > contractAmount;
	// Whether contract is exceeded once not-in-budget logged time is folded
	// in too — used everywhere else, since that's the fuller picture.
	const overContract = contractAmount != null && fullGrandTotal > contractAmount;
	// A fee schedule determines this budget's rates, so it has to be picked
	// before any line can be added — not just before the final save.
	const needsFeeSchedule = !bqeBudgetId && !feeScheduleId;
	// The parent tab's own loading gate only covers the "budgets"/"time_entries"
	// phase-report sections — it has no visibility into this panel's own live
	// BQE budget fetch, so the panel needs its own gate too. Without it, the
	// info box/progress bars/line items can render before that fetch resolves:
	// real budget lines are missing and every logged resource briefly looks
	// "not in budget." Catalog loading (activity/expense/resource/fee schedule)
	// is deliberately NOT included here — those pickers already show their own
	// in-place loading spinner and stay usable while they load, and gating the
	// whole page on all four of them just makes the page hang longer for no
	// benefit (they were never the cause of the raw-id/not-in-budget bug).
	const pageLoading = lineItemsLoading;

	// Each expense line's markup per BQE's current values. The server only sends
	// BQE the expense lines being added/edited/deleted, so untouched lines keep
	// their markups — but an edited line is recalculated at 0%, so a line with a
	// markup can't be edited (its edit button is disabled; this blocks the save
	// as a backstop). Under 0.5% is rounding noise, not a real markup.
	const markupById = new Map(baseline.expense.map((r) => [r.id, Number(r.markup) || 0]));
	const markedUpExpenseLines = diff.edited
		.filter((d) => d.kind === "expense" && Math.abs(markupById.get(d.row.id) || 0) >= 0.5)
		.map((d) => ({ ...d.row, markup: markupById.get(d.row.id) }));
	const saveBlockedByMarkup = markedUpExpenseLines.length > 0;

	const warnings = useMemo(() => {
		const list = [];
		if (chargeMismatches.length) {
			list.push({
				key: "charge-mismatch",
				danger: true,
				text: `After saving, these expense lines don't match what they should be in BQE: ${chargeMismatches.map((m) => m.missing ? `${m.label} (no longer in BQE)` : `${m.label} (expected ${fmtCurrency(m.sent)}, BQE has ${fmtCurrency(m.kept)})`).join(", ")}. Check this budget in BQE.`,
			});
		}
		if (saveBlockedByMarkup) {
			list.push({
				key: "expense-markup",
				danger: true,
				text: `You've edited ${markedUpExpenseLines.length === 1 ? "an expense line that has" : "expense lines that have"} a markup (${markedUpExpenseLines.map((r) => `${displayItemName(r.itemName) || r.resourceName}: ${fmtPercent(r.markup)}`).join(", ")}). Saving would reset ${markedUpExpenseLines.length === 1 ? "its" : "their"} markup to 0% in BQE, so undo ${markedUpExpenseLines.length === 1 ? "that edit" : "those edits"} and make ${markedUpExpenseLines.length === 1 ? "it" : "them"} in BQE. Other lines can still be added, edited or deleted here.`,
			});
		}
		if (overContract) {
			list.push({
				key: "over-contract",
				danger: true,
				text: combinedUnbudgetedAmountTotal > 0
					? `Full total ${fmtCurrency(fullGrandTotal)} (${fmtCurrency(grandTotal)} budgeted plus ${fmtCurrency(combinedUnbudgetedAmountTotal)} not yet in budget) exceeds the phase contract amount ${fmtCurrency(contractAmount)} by ${fmtCurrency(fullGrandTotal - contractAmount)}. An amendment would be required.`
					: `Budgeted total ${fmtCurrency(grandTotal)} exceeds the phase contract amount ${fmtCurrency(contractAmount)} by ${fmtCurrency(grandTotal - contractAmount)}. An amendment would be required.`,
			});
		}
		return list;
	}, [overContract, grandTotal, fullGrandTotal, combinedUnbudgetedAmountTotal, contractAmount, chargeMismatches, diff, baseline.expense]); // eslint-disable-line react-hooks/exhaustive-deps

	// Hover detail for the Total Services amount — the contract amount and how
	// the budgeted (and, if any, not-in-budget) total compares to it, without
	// having to go looking for the warning banner below.
	const contractAmountTooltip = contractAmount == null ? "No contract amount recorded for this phase." : (
		<div>
			<div>Contract Amount: {fmtCurrency(contractAmount)}</div>
			<div>Budgeted Total: {fmtCurrency(grandTotal)} ({fmtCurrency(Math.abs(grandTotal - contractAmount))} {grandTotal > contractAmount ? "over" : "under"} contract)</div>
			{combinedUnbudgetedAmountTotal > 0 && <div>Full Total (incl. not in budget): {fmtCurrency(fullGrandTotal)}</div>}
		</div>
	);

	// Real Budget service/expense lines don't carry a resourceType — it's only
	// known once a row's resource is (re)picked through the Autocomplete, which
	// stamps it from the catalog. Existing lines loaded straight from BQE fall
	// back to a live resourceCatalog lookup by resourceId so "Employee"/"Vendor"
	// still shows instead of N/A.
	const resourceTypeFor = (row) => {
		if (row.resourceType) return row.resourceType;
		const match = row.resourceId && resourceCatalog.find((r) => idsMatch(r.id, row.resourceId));
		return match?.type || "";
	};

	// ── Row action helpers ─────────────────────────────────────────────────
	const rowLabel = (kind, row) => {
		if (kind === "service") return `${row.resourceName || "This line"} · ${displayItemName(row.activityName) || "Untitled"}`;
		return `${row.resourceName || "This line"} · ${displayItemName(row.itemName) || row.description || "Untitled"}`;
	};

	const isEditingRow = (kind, id) => editingByKind[kind]?.id === id;

	// A brand-new line (from "Add Service/Expense Line") only lives in the draft
	// until it's committed — it has no entry in `present` yet, so it can't be
	// found by mapping over the row list and must be appended separately.
	const newDraftRow = (kind) =>
		editingByKind[kind] && draftByKind[kind] && !present[kind].some((r) => r.id === editingByKind[kind].id) ? draftByKind[kind] : null;

	// ── Render: Service rows ───────────────────────────────────────────────
	const renderServiceSection = () => (
		<div>
			<div className="projectViewerBudgetSectionLabel">
				<BadgeIcon fontSize="inherit" />
				Services
			</div>
			<div className="projectViewerBudgetGridService projectViewerBudgetHeaderRow">
				<div>Type</div>
				<div>Resource</div>
				<div>Activity</div>
				<div>Description</div>
				<div>Hours Budgeted</div>
				<div>Rate</div>
				<div>Amount Budgeted</div>
				<div>Spent Hours</div>
				<div>Spent Amount</div>
				<div style={{ textAlign: "right" }}>Actions</div>
			</div>

			{present.service.length === 0 && !newDraftRow("service") && (
				<div className="projectViewerEmptyCell" style={{ padding: "22px 0" }}>No service line items yet.</div>
			)}
			{present.service.map((row) => (isEditingRow("service", row.id) ? renderServiceEditRow(row) : renderServiceReadRow(row)))}
			{newDraftRow("service") && renderServiceEditRow(newDraftRow("service"))}

			<button
				type="button"
				className="projectViewerBudgetGridService projectViewerAddLineRow"
				disabled={!!editingByKind.service || needsFeeSchedule}
				title={editingByKind.service ? "Finish editing the current line first" : needsFeeSchedule ? "Select a fee schedule above first" : undefined}
				onClick={() => startAddService()}
			>
				<span className="projectViewerAddLineCell"><AddIcon fontSize="inherit" />Add Service Line</span>
			</button>

			{unbudgetedWithPreview.length > 0 && (
				<>
					<div className="projectViewerUnbudgetedHeader">Not In Budget</div>
					{unbudgetedWithPreview.map((u) => {
						// u.key is already `id:<uuid>` whenever the time entries behind this
						// resource carried a real BQE resource id — a far more reliable
						// source than matching resourceCatalog by name (formatting can
						// differ enough between the two to miss a match entirely).
						const { catalogMatch, activityMatch, resolvedResourceId, previewRate, previewAmount, spentAmount } = u;
						const addDisabled = !!editingByKind.service || needsFeeSchedule || feeScheduleLoading;
						return (
						<div key={u.key} className="projectViewerBudgetGridService projectViewerUnbudgetedRow">
							<div className="projectViewerBudgetMuted">{catalogMatch?.type || u.resource?.type || notApplicable}</div>
							<div>
								<div style={{ fontWeight: 500, color: "#b45309" }}>{u.resource?.name || u.key}</div>
							</div>
							<div className="projectViewerBudgetMuted">{displayItemName(u.billedActivity) || notApplicable}</div>
							<div className="projectViewerBudgetMuted">{activityDescriptionFor(activityMatch, u.billedActivity) || "Time logged with no budget line"}</div>
							<div className="projectViewerBudgetMuted">{notApplicable}</div>
							<div className="projectViewerBudgetMuted">
								{feeScheduleLoading ? <CircularProgress size={12} thickness={5} /> : previewRate != null ? fmtCurrency(previewRate) : notApplicable}
							</div>
							<div style={{ fontWeight: 600, color: "#b45309" }}>
								{feeScheduleLoading ? <CircularProgress size={12} thickness={5} /> : previewAmount != null ? fmtCurrency(previewAmount) : notApplicable}
							</div>
							<div style={{ fontWeight: 600, color: "#b45309" }}>
								<button type="button" className="spentHoursLinkButton" style={{ color: "inherit" }} onClick={() => setSpentHoursDialog({ key: u.key })}>{fmtHours(u.hours)}</button>
							</div>
							<div style={{ fontWeight: 600, color: "#b45309" }}>
								{feeScheduleLoading ? <CircularProgress size={12} thickness={5} /> : spentAmount != null ? fmtCurrency(spentAmount) : notApplicable}
							</div>
							<div style={{ display: "flex", justifyContent: "flex-end" }}>
								<Tooltip title={editingByKind.service ? "Finish editing the current line first" : needsFeeSchedule ? "Select a fee schedule above first" : feeScheduleLoading ? "Loading fee schedule rates…" : "Add this resource to the budget"} arrow>
									<span>
									<IconButton
										size="small"
										disabled={addDisabled}
										onClick={() => {
											startAddService({
												resourceKey: u.key,
												resourceId: resolvedResourceId,
												resourceName: catalogMatch?.name || u.resource?.name || "",
												resourceType: catalogMatch?.type || u.resource?.type || "",
												activityId: activityMatch?.id || "",
												activityName: activityMatch?.name || u.billedActivity || "",
												activityItemType: activityMatch?.itemType ?? null,
												description: activityDescriptionFor(activityMatch, u.billedActivity),
												rate: previewRate ?? "",
												hours: Math.round((u.hours || 0) * 100) / 100,
											});
										}}
									>
										<AddCircleOutlineIcon fontSize="small" style={{ color: addDisabled ? "rgba(0, 0, 0, 0.26)" : "#1976d2" }} />
									</IconButton>
									</span>
								</Tooltip>
							</div>
						</div>
					);
					})}
				</>
			)}

			<div className="projectViewerBudgetGridService projectViewerBudgetTotalsRow">
				<div style={{ fontWeight: 700 }}>Total Services</div>
				<div /><div /><div />
				<div style={{ fontWeight: 700 }}>{fmtHours(serviceHoursTotal)}</div>
				<div />
				<Tooltip title={contractAmountTooltip} arrow>
					<div style={{ fontWeight: 700, color: spentServicesTotal > serviceFeeTotal ? "#c62828" : "#2e7d32" }}>
						{fmtCurrency(serviceFeeTotal + spentUnbudgetedServicesTotal)}
						{spentUnbudgetedServicesTotal > 0 && (
							<div className="projectViewerBudgetSubtext" style={{ fontWeight: 600 }}>
								Budgeted {fmtCurrency(serviceFeeTotal)} + Not In Budget {fmtCurrency(spentUnbudgetedServicesTotal)}
							</div>
						)}
					</div>
				</Tooltip>
				<div style={{ fontWeight: 700, color: serviceLoggedTotal > serviceHoursTotal ? "#c62828" : "#2e7d32" }}>
					{allLoggedHoursTotal > 0 ? (
						<button type="button" className="spentHoursLinkButton" style={{ color: "inherit" }} onClick={() => setSpentHoursDialog({ key: null })}>{fmtHours(serviceLoggedTotal)}</button>
					) : fmtHours(serviceLoggedTotal)}
					{allLoggedHoursTotal !== serviceLoggedTotal && (
						<div className="projectViewerBudgetSubtext" style={{ fontWeight: 600 }}>+ {fmtHours(allLoggedHoursTotal - serviceLoggedTotal)} not in budget</div>
					)}
				</div>
				<div style={{ fontWeight: 700, color: spentServicesTotal > serviceFeeTotal ? "#c62828" : "#2e7d32" }}>
					{fmtCurrency(spentServicesTotal)}
					{spentUnbudgetedServicesTotal > 0 && (
						<div className="projectViewerBudgetSubtext" style={{ fontWeight: 600 }}>
							In Budget {fmtCurrency(serviceSpentAmountTotal)} + Not In Budget {fmtCurrency(spentUnbudgetedServicesTotal)}
						</div>
					)}
				</div>
				<div />
			</div>
		</div>
	);

	const renderServiceReadRow = (row) => {
		const logged = row.resourceKey ? (resourceContext.hoursByKey.get(row.resourceKey) || 0) : 0;
		// Deleted lines aren't in serviceLineSpend; they show nothing spent.
		const lineSpend = serviceLineSpend.get(row.id) || { hours: 0, amount: 0, entries: [] };
		const overLine = lineSpend.hours > (Number(row.hours) || 0);
		const deleteDisabled = logged > 0;
		return (
			<div key={row.id} className={`projectViewerBudgetRow${row.deleted ? " deleted" : ""}`}>
				<div className="projectViewerBudgetGridService">
					<div className="projectViewerBudgetMuted">{resourceTypeFor(row) || notApplicable}</div>
					<div>
						<div className={row.deleted ? "projectViewerBudgetMuted" : undefined}>{row.resourceName || notApplicable}</div>
					</div>
					<div className="projectViewerBudgetMuted">{displayItemName(row.activityName) || notApplicable}</div>
					<div className="projectViewerBudgetMuted">{row.description || notApplicable}</div>
					<div>{fmtHours(row.hours)}</div>
					<div className="projectViewerBudgetMuted">{row.rate ? fmtCurrency(row.rate) : notApplicable}</div>
					<div style={{ fontWeight: 600 }}>{fmtCurrency(serviceAmount(row))}</div>
					<div style={{ color: overLine ? "#c62828" : undefined, fontWeight: overLine ? 600 : undefined }}>
						{!row.resourceKey ? notApplicable : lineSpend.hours > 0 ? (
							<button
								type="button"
								className="spentHoursLinkButton"
								style={overLine ? { color: "inherit" } : undefined}
								onClick={() => setSpentHoursDialog({ key: row.resourceKey, line: { label: displayItemName(row.activityName), entries: lineSpend.entries } })}
							>
								{fmtHours(lineSpend.hours)}
							</button>
						) : fmtHours(lineSpend.hours)}
					</div>
					<div style={{ color: overLine ? "#c62828" : undefined, fontWeight: overLine ? 600 : undefined }}>
						{row.resourceKey ? fmtCurrency(lineSpend.amount) : notApplicable}
					</div>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
						{row.deleted ? (
							<Tooltip title="Restore line" arrow>
								<IconButton size="small" onClick={() => restoreRow("service", row.id)}>
									<RestoreFromTrashIcon fontSize="small" style={{ color: "#1976d2" }} />
								</IconButton>
							</Tooltip>
						) : (
							<>
								<Tooltip title={editingByKind.service ? "Finish editing the current line first" : "Edit line"} arrow>
									<span>
										<IconButton size="small" disabled={!!editingByKind.service} onClick={() => startEdit("service", row)}>
											<EditIcon fontSize="small" />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip
									title={deleteDisabled
										? `Can't delete this line. ${fmtHours(logged)} hours are already logged against this resource in BQE. Reduce the budgeted hours instead, or remove the time entries first.`
										: "Delete line"}
									arrow
								>
									<span>
										<IconButton
											size="small"
											disabled={deleteDisabled}
											onClick={() => requestDelete("service", row)}
										>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</span>
								</Tooltip>
							</>
						)}
					</div>
				</div>
			</div>
		);
	};

	const renderServiceEditRow = (row) => {
		const draft = draftByKind.service;
		const isNewRow = !draft.bqeId;
		const canApply = !!draft.resourceId && (!isNewRow || !!draft.activityId) && draft.hours !== "";
		const applyDisabledReason = [
			!draft.resourceId && "select a resource",
			isNewRow && !draft.activityId && "select a real activity from the list (typing alone isn't enough)",
			draft.hours === "" && "enter hours",
		].filter(Boolean).join("; ");
		const resourceOptions = withCurrentOption(resourceCatalog, draft.resourceId, draft.resourceName);
		const activityOptions = withCurrentOption(activityCatalog, draft.activityId, draft.activityName);
		return (
			<div key={row.id} className="projectViewerBudgetRow editing">
				<div className="projectViewerBudgetGridService">
					<div className="projectViewerBudgetMuted">{resourceTypeFor(draft) || notApplicable}</div>
					<Autocomplete
						size="small"
						options={resourceOptions}
						loading={catalogsLoading}
						loadingText="Loading resources…"
						getOptionLabel={(o) => (o?.name ? RESOURCE_SELECT_LABEL(o) : "")}
						isOptionEqualToValue={(o, v) => idsMatch(o.id, v.id)}
						value={resourceOptions.find((r) => idsMatch(r.id, draft.resourceId)) || null}
						onChange={(e, option) => {
							setDraftForKind("service", (d) => {
								const resourceId = option?.id || "";
								const fsRate = feeScheduleRateFor("service", resourceId, d.activityId);
								return { ...d, resourceId, resourceName: option?.name || "", resourceType: option?.type || "", resourceIsGroup: !!option?.isGroup, rate: d.rate === "" && fsRate != null ? fsRate : d.rate };
							});
						}}
						renderInput={(params) => <TextField {...params} placeholder="Select resource…" />}
					/>
					<Autocomplete
						size="small"
						options={activityOptions}
						loading={catalogsLoading}
						loadingText="Loading activities…"
						getOptionLabel={(o) => (o?.name ? ITEM_SELECT_LABEL(o) : "")}
						isOptionEqualToValue={(o, v) => idsMatch(o.id, v.id)}
						value={activityOptions.find((a) => idsMatch(a.id, draft.activityId)) || null}
						onChange={(e, option) => {
							setDraftForKind("service", (d) => {
								const activityId = option?.id || "";
								const fallbackRate = feeScheduleRateFor("service", d.resourceId, activityId) ?? option?.billRate ?? resourceRateFallback(d.resourceKey);
								return {
									...d,
									activityId,
									activityName: option?.name || "",
									activityItemType: option?.itemType ?? null,
									rate: d.rate === "" && fallbackRate != null ? fallbackRate : d.rate,
									description: d.description === "" && option?.name ? activityDescriptionFor(option, "") : d.description,
								};
							});
						}}
						renderInput={(params) => <TextField {...params} placeholder="Select activity…" />}
					/>
					<TextField
						size="small"
						placeholder="Description"
						value={draft.description}
						onChange={(e) => setDraftForKind("service", (d) => ({ ...d, description: e.target.value }))}
					/>
					<TextField
						size="small"
						type="number"
						value={draft.hours}
						onChange={(e) => setDraftForKind("service", (d) => ({ ...d, hours: e.target.value }))}
					/>
					<TextField
						size="small"
						value={draft.rate}
						onChange={(e) => { if (isValidMoneyInput(e.target.value)) setDraftForKind("service", (d) => ({ ...d, rate: e.target.value })); }}
						{...moneyFieldProps}
					/>
					<div style={{ fontWeight: 600 }}>{fmtCurrency(serviceAmount(draft))}</div>
					{/* An existing line keeps its own share; a new one previews the resource's total. */}
					<div className="projectViewerBudgetMuted">{!draft.resourceKey ? notApplicable : fmtHours(serviceLineSpend.get(row.id)?.hours ?? (resourceContext.hoursByKey.get(draft.resourceKey) || 0))}</div>
					<div className="projectViewerBudgetMuted">{!draft.resourceKey ? notApplicable : fmtCurrency(serviceLineSpend.get(row.id)?.amount ?? spentAmountFor(resourceContext, draft.resourceKey, draft.rate))}</div>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
						<Tooltip title={canApply ? "Apply" : `Still needed: ${applyDisabledReason}`} arrow>
							<span>
								<IconButton size="small" disabled={!canApply} onClick={() => commitEdit("service")} className="projectViewerBudgetApplyButton">
									<CheckIcon fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Cancel" arrow>
							<IconButton size="small" onClick={() => cancelEdit("service")}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</div>
				</div>
			</div>
		);
	};

	// ── Render: Expense rows ───────────────────────────────────────────────
	const renderExpenseSection = () => (
		<div>
			<div className="projectViewerBudgetSectionLabel">
				<ReceiptLongIcon fontSize="inherit" />
				Expenses
			</div>
			<div className="projectViewerBudgetGridExpense projectViewerBudgetHeaderRow">
				<div>Item</div>
				<div>Resource</div>
				<div>Cost Rate</div>
				<div>Units</div>
				<Tooltip arrow title="Markup can only be set in BQE.">
					<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
						Markup
						<InfoOutlinedIcon className="projectViewerInfoIcon" />
					</div>
				</Tooltip>
				<div>Charge</div>
				<div style={{ textAlign: "right" }}>Actions</div>
			</div>

			{present.expense.length === 0 && !newDraftRow("expense") && (
				<div className="projectViewerEmptyCell" style={{ padding: "22px 0" }}>No expense line items yet.</div>
			)}
			{sortExpensesByItem(present.expense).map((row) => (isEditingRow("expense", row.id) ? renderExpenseEditRow(row) : renderExpenseReadRow(row)))}
			{newDraftRow("expense") && renderExpenseEditRow(newDraftRow("expense"))}

			<button
				type="button"
				className="projectViewerBudgetGridExpense projectViewerAddLineRow"
				disabled={!!editingByKind.expense || needsFeeSchedule}
				title={editingByKind.expense ? "Finish editing the current line first" : needsFeeSchedule ? "Select a fee schedule above first" : undefined}
				onClick={startAddExpense}
			>
				<span className="projectViewerAddLineCell"><AddIcon fontSize="inherit" />Add Expense Line</span>
			</button>

			{unbudgetedExpensesWithPreview.length > 0 && (
				<>
					<div className="projectViewerUnbudgetedHeader">Not In Budget</div>
					{[...unbudgetedExpensesWithPreview].sort(byItemName((u) => u.billedItem)).map((u) => {
						const { catalogMatch, itemMatch, resolvedResourceId, previewCostRate } = u;
						const addDisabled = !!editingByKind.expense || needsFeeSchedule;
						return (
							<div key={u.key} className="projectViewerBudgetGridExpense projectViewerUnbudgetedRow">
								<div style={{ fontWeight: 500, color: "#b45309" }}>{displayItemName(u.billedItem) || notApplicable}</div>
								<div>
									<div style={{ fontWeight: 500, color: "#b45309" }}>{u.resource?.name || u.key}</div>
									<div className="projectViewerBudgetSubtext">{catalogMatch?.type || u.resource?.type || ""}</div>
								</div>
								<div className="projectViewerBudgetMuted">{previewCostRate != null ? fmtCurrency(previewCostRate) : notApplicable}</div>
								<div className="projectViewerBudgetMuted">{u.units || notApplicable}</div>
								<div className="projectViewerBudgetMuted">{notApplicable}</div>
								<div style={{ fontWeight: 600, color: "#b45309" }}>{fmtCurrency(u.charge)}</div>
								<div style={{ display: "flex", justifyContent: "flex-end" }}>
									<Tooltip title={editingByKind.expense ? "Finish editing the current line first" : needsFeeSchedule ? "Select a fee schedule above first" : "Add this resource to the budget"} arrow>
										<span>
										<IconButton
											size="small"
											disabled={addDisabled}
											onClick={() => {
												startAddExpense({
													resourceKey: u.key,
													resourceId: resolvedResourceId,
													resourceName: catalogMatch?.name || u.resource?.name || "",
													itemId: itemMatch?.id || "",
													itemName: itemMatch?.name || u.billedItem || "",
													itemType: itemMatch?.itemType ?? null,
													description: displayItemName(itemMatch?.name || u.billedItem) || "",
													cost: previewCostRate ?? "",
													units: Math.round((u.units || 0) * 100) / 100 || 1,
												});
											}}
										>
											<AddCircleOutlineIcon fontSize="small" style={{ color: addDisabled ? "rgba(0, 0, 0, 0.26)" : "#1976d2" }} />
										</IconButton>
										</span>
									</Tooltip>
								</div>
							</div>
						);
					})}
				</>
			)}

			<div className="projectViewerBudgetGridExpense projectViewerBudgetTotalsRow">
				<div style={{ fontWeight: 700 }}>Total Expenses</div>
				<div />
				<div style={{ fontWeight: 700 }}>{fmtCurrency(expenseCostTotal)}</div>
				<div />
				<div />
				<div style={{ fontWeight: 700 }}>
					{fmtCurrency(expenseChargeTotal)}
					{unbudgetedExpenseAmountTotal > 0 && (
						<div className="projectViewerBudgetSubtext" style={{ fontWeight: 600 }}>+ {fmtCurrency(unbudgetedExpenseAmountTotal)} not in budget</div>
					)}
				</div>
				<div />
			</div>
		</div>
	);

	const renderExpenseReadRow = (row) => (
		<div key={row.id} className={`projectViewerBudgetRow${row.deleted ? " deleted" : ""}`}>
			<div className="projectViewerBudgetGridExpense">
				<div>
					<div className={row.deleted ? "projectViewerBudgetMuted" : undefined}>{displayItemName(row.itemName) || notApplicable}</div>
					{row.description && <div className="projectViewerBudgetSubtext">{row.description}</div>}
				</div>
				<div className="projectViewerBudgetMuted">{row.resourceName || notApplicable}</div>
				<div className="projectViewerBudgetMuted">{row.cost !== "" && row.cost != null ? fmtCurrency(row.cost) : notApplicable}</div>
				<div className="projectViewerBudgetMuted">{row.units ?? notApplicable}</div>
				<div className="projectViewerBudgetMuted">{fmtPercent(row.markup)}</div>
				<div style={{ fontWeight: 600 }}>{fmtCurrency(expenseCharge(row))}</div>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
					{row.deleted ? (
						<Tooltip title="Restore line" arrow>
							<IconButton size="small" onClick={() => restoreRow("expense", row.id)}>
								<RestoreFromTrashIcon fontSize="small" style={{ color: "#1976d2" }} />
							</IconButton>
						</Tooltip>
					) : (
						<>
							{(() => {
								// BQE resets an edited line's markup to 0% (see expenseCharge's comment).
								const hasMarkup = Math.abs(markupById.get(row.id) || 0) >= 0.5;
								return (
									<Tooltip
										arrow
										title={hasMarkup
											? "This line has a markup, so it can only be edited in BQE."
											: editingByKind.expense ? "Finish editing the current line first" : "Edit line"}
									>
										<span>
											<IconButton size="small" disabled={hasMarkup || !!editingByKind.expense} onClick={() => startEdit("expense", row)}>
												<EditIcon fontSize="small" />
											</IconButton>
										</span>
									</Tooltip>
								);
							})()}
							<Tooltip title="Delete line" arrow>
								<IconButton size="small" onClick={() => requestDelete("expense", row)}>
									<DeleteOutlineIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</>
					)}
				</div>
			</div>
		</div>
	);

	const renderExpenseEditRow = (row) => {
		const draft = draftByKind.expense;
		const isNewRow = !draft.bqeId;
		const canApply = !!draft.resourceId && (!isNewRow || !!draft.itemId) && draft.units !== "";
		const applyDisabledReason = [
			!draft.resourceId && "select a resource",
			isNewRow && !draft.itemId && "select a real expense item from the list (typing alone isn't enough)",
			draft.units === "" && "enter units",
		].filter(Boolean).join("; ");
		const resourceOptions = withCurrentOption(resourceCatalog, draft.resourceId, draft.resourceName);
		const itemOptions = withCurrentOption(expenseCatalog, draft.itemId, draft.itemName);
		return (
			<div key={row.id} className="projectViewerBudgetRow editing">
				<div className="projectViewerBudgetGridExpense">
					<Autocomplete
						size="small"
						options={itemOptions}
						loading={catalogsLoading}
						loadingText="Loading expense items…"
						getOptionLabel={(o) => (o?.name ? ITEM_SELECT_LABEL(o) : "")}
						isOptionEqualToValue={(o, v) => idsMatch(o.id, v.id)}
						value={itemOptions.find((o) => idsMatch(o.id, draft.itemId)) || null}
						onChange={(e, option) => {
							setDraftForKind("expense", (d) => {
								const itemId = option?.id || "";
								const fsRate = feeScheduleRateFor("expense", d.resourceId, itemId) ?? option?.costRate;
								return {
									...d,
									itemId,
									itemName: option?.name || "",
									itemType: option?.itemType ?? null,
									cost: d.cost === "" && fsRate != null ? fsRate : d.cost,
								};
							});
						}}
						renderInput={(params) => <TextField {...params} placeholder="Select expense item…" />}
					/>
					<Autocomplete
						size="small"
						options={resourceOptions}
						loading={catalogsLoading}
						loadingText="Loading resources…"
						getOptionLabel={(o) => (o?.name ? RESOURCE_SELECT_LABEL(o) : "")}
						isOptionEqualToValue={(o, v) => idsMatch(o.id, v.id)}
						value={resourceOptions.find((r) => idsMatch(r.id, draft.resourceId)) || null}
						onChange={(e, option) => {
							setDraftForKind("expense", (d) => {
								const resourceId = option?.id || "";
								const fsRate = feeScheduleRateFor("expense", resourceId, d.itemId);
								return { ...d, resourceId, resourceName: option?.name || "", resourceIsGroup: !!option?.isGroup, cost: d.cost === "" && fsRate != null ? fsRate : d.cost };
							});
						}}
						renderInput={(params) => <TextField {...params} placeholder="Select resource…" />}
					/>
					<TextField
						size="small"
						placeholder="0.00"
						value={draft.cost}
						onChange={(e) => { if (isValidMoneyInput(e.target.value)) setDraftForKind("expense", (d) => ({ ...d, cost: e.target.value })); }}
						{...moneyFieldProps}
					/>
					<TextField size="small" type="number" value={draft.units} onChange={(e) => setDraftForKind("expense", (d) => ({ ...d, units: e.target.value }))} />
					<Tooltip arrow title="Markup can only be set in BQE.">
						<div className="projectViewerBudgetMuted">{fmtPercent(draft.markup)}</div>
					</Tooltip>
					<div style={{ fontWeight: 600 }}>{fmtCurrency(expenseCharge(draft))}</div>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
						<Tooltip title={canApply ? "Apply" : `Still needed: ${applyDisabledReason}`} arrow>
							<span>
								<IconButton size="small" disabled={!canApply} onClick={() => commitEdit("expense")} className="projectViewerBudgetApplyButton">
									<CheckIcon fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Cancel" arrow>
							<IconButton size="small" onClick={() => cancelEdit("expense")}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</div>
				</div>
				<TextField
					size="small"
					placeholder="Notes (optional)"
					value={draft.description}
					onChange={(e) => setDraftForKind("expense", (d) => ({ ...d, description: e.target.value }))}
					style={{ marginTop: 6 }}
					fullWidth
				/>
			</div>
		);
	};

	return (
		<div className="projectViewerTabPanel">
			{catalogsError && (
				<div className="projectViewerBudgetWarning">
					<WarningAmberIcon style={{ fontSize: 18 }} />
					<div>{catalogsError}</div>
				</div>
			)}
			{lineItemsStale && (
				<div className="projectViewerBudgetWarning">
					<WarningAmberIcon style={{ fontSize: 18 }} />
					<div>Couldn't reach BQE for a live read. Line items below are from the hourly-synced snapshot and may not reflect the latest changes in BQE. Edits still save normally.</div>
				</div>
			)}

			{pageLoading ? (
				<div className="projectViewerPaper">
					<div className="projectViewerPaperBody">{renderLoadingNotice()}</div>
				</div>
			) : (
				<>

			<div className="projectViewerPaper">
				<div className="projectViewerPaperHeader projectViewerCardHeaderWithAction">
					<div>
						<div className="projectViewerHeaderTitleRow">
							<h3>Budget</h3>
							<Tooltip title="Budgeted Service Hours, Budgeted Total, and Budget In BQE Created are fetched live from BQE when this tab opens. Spent To Date is priced from synced BQE time and expense entries. Budget Status, All-time Logged Hours, and Budget Last Updated come from BQE's hourly audit sync and may be up to an hour old." arrow>
								<InfoOutlinedIcon className="projectViewerInfoIcon" />
							</Tooltip>
						</div>
						<p>Totals live · status hourly</p>
					</div>
					<a
						className={`projectViewerQuickLinkItem projectViewerInlineDealLink${!bqeBudgetUrl ? " disabled" : ""}`}
						href={bqeBudgetUrl || undefined}
						target="_blank"
						rel="noreferrer"
					>
						<div className="projectViewerIntegrationBadge bqe">BQ</div>
						<div className="projectViewerQuickLinkTitle">BQE Budget</div>
						<LaunchIcon fontSize="small" />
					</a>
				</div>
				<div className="projectViewerPaperBody">
					<div className="projectViewerBudgetKeyValueGrid">
						<div className="projectViewerKey">Contract Type</div>
						<div className="projectViewerValue">{atAGlanceSection.contract_type || notApplicable}</div>
						<div className="projectViewerKey">Budget In BQE Created</div>
						<div className="projectViewerValue" style={{ color: bqeBudgetId ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
							{bqeBudgetId ? "Yes" : "No"}
						</div>
						<div className="projectViewerKey">Contract Amount</div>
						<div className="projectViewerValue">{contractAmount != null ? fmtCurrency(contractAmount) : notApplicable}</div>
						<div className="projectViewerKey">Budgeted Total</div>
						<div className="projectViewerValue" style={{ fontWeight: 600, color: overContract ? "#c62828" : undefined }}>
							{fmtCurrency(grandTotal)}
							{combinedUnbudgetedAmountTotal > 0 && (
								<div className="projectViewerBudgetSubtext" style={{ fontWeight: 600 }}>+ {fmtCurrency(combinedUnbudgetedAmountTotal)} not in budget</div>
							)}
						</div>
						<div className="projectViewerKey" style={{ display: "flex", alignItems: "center", gap: 4 }}>
							Spent To Date
							<Tooltip arrow title="All time (billable and non-billable) and posted expenses on this phase, in the budget or not — the same basis as BQE's budget spent. Each time entry is priced at its own bill rate.">
								<InfoOutlinedIcon className="projectViewerInfoIcon" />
							</Tooltip>
						</div>
						<div className="projectViewerValue" style={{
							fontWeight: 600,
							color: contractAmount != null && spentToDateTotal > contractAmount ? "#c62828"
								: spentToDateTotal > grandTotal ? "#b45309"
								: undefined,
						}}>
							{(feeScheduleLoading || catalogsLoading) && spentUnbudgeted.length > 0 ? <CircularProgress size={12} thickness={5} /> : fmtCurrency(spentToDateTotal)}
							<div className="projectViewerBudgetSubtext" style={{ fontWeight: 600 }}>
								Services {fmtCurrency(spentServicesTotal)} + Expenses {fmtCurrency(spentExpensesTotal)}
							</div>
							{unpricedUnbudgetedHours > 0 && (
								<div className="projectViewerBudgetSubtext" style={{ fontWeight: 600 }}>{fmtHours(unpricedUnbudgetedHours)} with no rate found (not included)</div>
							)}
						</div>
						<div className="projectViewerKey">Budget Status</div>
						<div className="projectViewerValue" style={{
							color: budgetsSection.budget_status === "Over Budget" ? "#c62828"
								: budgetsSection.budget_status === "Under Budget" || budgetsSection.budget_status === "On Budget" ? "#2e7d32"
								: undefined,
							fontWeight: 600,
						}}>
							{budgetsSection.budget_status || "Unknown"}
						</div>
						<div className="projectViewerKey">Budgeted Service Hours</div>
						<div className="projectViewerValue">
							{fmtHours(serviceHoursTotal)}
							{allLoggedHoursTotal !== serviceLoggedTotal && (
								<div className="projectViewerBudgetSubtext" style={{ fontWeight: 600 }}>+ {fmtHours(allLoggedHoursTotal - serviceLoggedTotal)} not in budget</div>
							)}
						</div>
						<div className="projectViewerKey">All-time Logged Hours</div>
						<div className="projectViewerValue">{fmtHours(budgetsSection.all_time_logged_hours)}</div>
						<div className="projectViewerKey">Budget Last Updated</div>
						<div className="projectViewerValue">{fmtTimestamp(budgetsSection.last_budget_update_timestamp)}</div>
					</div>

					<div className="projectViewerBudgetProgressBars">
						<ProgressStackBar
							title="Hours"
							target={serviceHoursTotal}
							formatValue={fmtHours}
							segments={[
								{ key: "in_budget", label: "Logged (In Budget)", value: serviceLoggedTotal, color: "#2e7d32" },
								{ key: "remaining", label: "Remaining (Budgeted)", value: Math.max(serviceHoursTotal - serviceLoggedTotal, 0), color: "#81c784" },
								{ key: "not_in_budget", label: "Logged (Not In Budget)", value: allLoggedHoursTotal - serviceLoggedTotal, color: "#b45309" },
							]}
						/>
						{contractAmount != null ? (
							<ProgressStackBar
								title="Finances"
								target={contractAmount}
								formatValue={fmtCurrency}
								segments={[
									{ key: "paid", label: "Invoiced & Paid", value: paidAmount, color: "#2e7d32" },
									{ key: "invoiced_unpaid", label: "Invoiced (Unpaid)", value: unpaidNotOverdueAmount, color: "#1976d2" },
									{ key: "overdue", label: "Invoiced (Overdue)", value: overdueAmount, color: "#c62828" },
									{ key: "budgeted_not_invoiced", label: "Budgeted (Not Invoiced)", value: budgetedNotInvoicedAmount, color: "#81c784" },
									{ key: "not_in_budget_services", label: "Not In Budget — Services", value: unbudgetedAmountTotal, color: "#b45309" },
									{ key: "not_in_budget_expenses", label: "Not In Budget — Expenses", value: unbudgetedExpenseAmountTotal, color: "#f0a202" },
								]}
							/>
						) : (
							<div className="projectViewerBudgetProgressBar">
								<div className="projectViewerBudgetProgressHeader"><span>Finances</span></div>
								<div className="projectViewerBudgetMuted">No contract amount recorded for this phase.</div>
							</div>
						)}
					</div>
				</div>
			</div>

			{warnings.map((w) => (
				<div key={w.key} className={`projectViewerBudgetWarning${w.danger ? " danger" : ""}`}>
					{w.danger ? <ErrorOutlineIcon style={{ fontSize: 18 }} /> : <WarningAmberIcon style={{ fontSize: 18 }} />}
					<div>{w.text}</div>
				</div>
			))}

			<div className="projectViewerPaper projectViewerBudgetTablesGrid">
				<div className="projectViewerPaperHeader projectViewerCommHeader">
					<div>
						<div className="projectViewerHeaderTitleRow">
							<h3>Line Items</h3>
						</div>
						<p>Fetched live · Save writes directly to BQE</p>
					</div>
					<div className="projectViewerMiniTabs" role="tablist">
						<button type="button" role="tab" aria-selected={subTab === "service"} className={`projectViewerMiniTab${subTab === "service" ? " active" : ""}`} onClick={() => setSubTab("service")}>
							Service
						</button>
						<button type="button" role="tab" aria-selected={subTab === "expense"} className={`projectViewerMiniTab${subTab === "expense" ? " active" : ""}`} onClick={() => setSubTab("expense")}>
							Expense
						</button>
					</div>
				</div>

				<div className={`projectViewerBudgetFeeScheduleRow${bqeBudgetId ? " readOnly" : ""}`}>
					<span className="projectViewerBudgetFeeScheduleLabel">Fee Schedule</span>
					{bqeBudgetId ? (
						<Tooltip title="Set when this budget was created in BQE. Used to price new lines and Not In Budget previews." arrow>
							<span className="projectViewerValue">
								{catalogsLoading ? "Loading…" : (feeScheduleCatalog.find((f) => f.id === feeScheduleId)?.name || "None set")}
							</span>
						</Tooltip>
					) : (
						<>
							<Autocomplete
								size="small"
								options={feeScheduleCatalog.filter((f) => f.isActive)}
								loading={catalogsLoading}
								loadingText="Loading fee schedules…"
								getOptionLabel={(o) => o?.name || ""}
								isOptionEqualToValue={(o, v) => idsMatch(o.id, v.id)}
								value={feeScheduleCatalog.find((f) => f.id === feeScheduleId) || null}
								onChange={(e, option) => setFeeScheduleId(option?.id || "")}
								renderInput={(params) => <TextField {...params} placeholder="Select a fee schedule…" />}
								style={{ maxWidth: 320, flex: 1 }}
							/>
							<span className="projectViewerBudgetSubtext">Determines bill/cost rates. Required before adding lines.</span>
						</>
					)}
				</div>

				<div className="projectViewerBudgetToolbar">
					<div className="projectViewerBudgetToolbarLeft">
						<div className={`projectViewerChangeChip${noChanges ? " clean" : " dirty"}`}>
							{noChanges ? "No changes" : `${changeCount} change${changeCount === 1 ? "" : "s"} staged`}
						</div>
						<div className="projectViewerBudgetToolbarHint">
							{!bqeBudgetId
								? (needsFeeSchedule ? "No budget in BQE yet. Select a fee schedule above to start adding lines." : noChanges ? "Add a line, then Create Budget." : "This will create a budget in BQE from the staged lines.")
								: noChanges ? "Edit a line to get started." : "Save to write these changes to BQE."}
						</div>
					</div>
					<div className="projectViewerBudgetToolbarActions">
						<Tooltip title="Undo" arrow>
							<span>
								<IconButton size="small" disabled={!state.past.length} onClick={() => dispatch({ type: "UNDO" })}>
									<UndoIcon fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Redo" arrow>
							<span>
								<IconButton size="small" disabled={!state.future.length} onClick={() => dispatch({ type: "REDO" })}>
									<RedoIcon fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<div className="projectViewerBudgetToolbarDivider" />
						<Button variant="outlined" color="inherit" size="small" disabled={noChanges} onClick={onDiscard}>Discard</Button>
						<Tooltip arrow title={saveBlockedByMarkup ? "Saving would reset the markup on an edited expense line to 0% in BQE. Make that edit in BQE instead." : ""}>
							<span>
								<Button variant="contained" size="small" disabled={noChanges || needsFeeSchedule || saveBlockedByMarkup} onClick={() => setSaveReviewOpen(true)}>{bqeBudgetId ? "Save Budget" : "Create Budget"}</Button>
							</span>
						</Tooltip>
					</div>
				</div>

				<div className="projectViewerPaperBody projectViewerCompactTableBody">
					{subTab === "service" ? renderServiceSection() : renderExpenseSection()}
				</div>
			</div>

				</>
			)}

			<SpentHoursDialog
				open={!!spentHoursDialog}
				onClose={() => setSpentHoursDialog(null)}
				timeEntries={timeEntries}
				keyFor={resourceContext.keyFor}
				initialKey={spentHoursDialog?.key ?? null}
				line={spentHoursDialog?.line ?? null}
			/>

			<Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
				<DialogTitle className="deleteLineDialogTitle">Delete Line</DialogTitle>
				<DialogContent className="deleteLineDialogContent">
					Delete {confirmDelete?.label}? It will be removed from the budget in BQE when you save.
					<div className="dialogButtons">
						<Button color="error" variant="outlined" onClick={() => setConfirmDelete(null)}>Cancel</Button>
						<Button color="error" variant="contained" onClick={() => deleteRow(confirmDelete.kind, confirmDelete.id)}>Delete Line</Button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={saveReviewOpen} onClose={() => (saving ? null : setSaveReviewOpen(false))} maxWidth="md" fullWidth>
				<DialogTitle>{bqeBudgetId ? "Review Budget Changes" : "Create Budget in BQE"}</DialogTitle>
				<DialogContent>
					<div className="projectViewerInfoBox" style={{ marginTop: 0 }}>
						{bqeBudgetId ? "Confirming below writes these changes to BQE right away." : "Confirming below creates a new budget in BQE right away and links it to this phase."}
					</div>
					{!bqeBudgetId && (
						<p className="projectViewerMutedText" style={{ marginTop: 0 }}>
							Fee Schedule: <strong>{feeScheduleCatalog.find((f) => f.id === feeScheduleId)?.name || notApplicable}</strong>
						</p>
					)}
					<p className="projectViewerMutedText">
						{changeCount} change{changeCount === 1 ? "" : "s"} staged across Services and Expenses. This is the full budget after those changes, with what's new/edited/removed highlighted.
					</p>

					{present.service.length > 0 && (
						<div className="projectViewerBudgetPreviewSection">
							<div className="projectViewerBudgetSectionLabel"><BadgeIcon fontSize="inherit" />Services</div>
							<div className="projectViewerBudgetPreviewGridService projectViewerBudgetHeaderRow">
								<div>Status</div>
								<div>Resource</div>
								<div>Activity</div>
								<div>Hours</div>
								<div>Rate</div>
								<div>Amount</div>
							</div>
							{present.service.map((row) => {
								const status = changeStatusById.get(`service:${row.id}`);
								const meta = status ? CHANGE_STATUS_META[status] : null;
								return (
									<div key={row.id} className={`projectViewerBudgetPreviewGridService projectViewerBudgetRow${status ? ` staged-${status}` : ""}`}>
										<div>{meta && <span className="projectViewerChangeStatusBadge" style={{ background: `${meta.color}1a`, color: meta.color }}>{meta.label}</span>}</div>
										<div>{row.resourceName || notApplicable}</div>
										<div>{displayItemName(row.activityName) || notApplicable}</div>
										<div>{fmtHours(row.hours)}</div>
										<div>{row.rate ? fmtCurrency(row.rate) : notApplicable}</div>
										<div style={{ fontWeight: 600 }}>{fmtCurrency(serviceAmount(row))}</div>
									</div>
								);
							})}
							<div className="projectViewerBudgetPreviewGridService projectViewerBudgetTotalsRow">
								<div style={{ fontWeight: 700 }}>Total</div>
								<div /><div />
								<div style={{ fontWeight: 700 }}>{fmtHours(serviceHoursTotal)}</div>
								<div />
								<div style={{ fontWeight: 700 }}>{fmtCurrency(serviceFeeTotal)}</div>
							</div>
						</div>
					)}

					{present.expense.length > 0 && (
						<div className="projectViewerBudgetPreviewSection">
							<div className="projectViewerBudgetSectionLabel"><ReceiptLongIcon fontSize="inherit" />Expenses</div>
							<div className="projectViewerBudgetPreviewGridExpense projectViewerBudgetHeaderRow">
								<div>Status</div>
								<div>Resource</div>
								<div>Item</div>
								<div>Units</div>
								<div>Cost Rate</div>
								<div>Markup</div>
								<div>Charge</div>
							</div>
							{sortExpensesByItem(present.expense).map((row) => {
								const status = changeStatusById.get(`expense:${row.id}`);
								const meta = status ? CHANGE_STATUS_META[status] : null;
								return (
									<div key={row.id} className={`projectViewerBudgetPreviewGridExpense projectViewerBudgetRow${status ? ` staged-${status}` : ""}`}>
										<div>{meta && <span className="projectViewerChangeStatusBadge" style={{ background: `${meta.color}1a`, color: meta.color }}>{meta.label}</span>}</div>
										<div>{row.resourceName || notApplicable}</div>
										<div>{displayItemName(row.itemName) || notApplicable}</div>
										<div>{row.units ?? notApplicable}</div>
										<div>{row.cost !== "" && row.cost != null ? fmtCurrency(row.cost) : notApplicable}</div>
										<div>{fmtPercent(row.markup)}</div>
										<div style={{ fontWeight: 600 }}>{fmtCurrency(expenseCharge(row))}</div>
									</div>
								);
							})}
							<div className="projectViewerBudgetPreviewGridExpense projectViewerBudgetTotalsRow">
								<div style={{ fontWeight: 700 }}>Total</div>
								<div /><div /><div />
								<div style={{ fontWeight: 700 }}>{fmtCurrency(expenseCostTotal)}</div>
								<div />
								<div style={{ fontWeight: 700 }}>{fmtCurrency(expenseChargeTotal)}</div>
							</div>
						</div>
					)}

					<div className="projectViewerKeyValueGrid" style={{ marginTop: 16 }}>
						<div className="projectViewerKey">Budgeted Total (after this save)</div>
						<div className="projectViewerValue" style={{ fontWeight: 700, color: budgetedOverContract ? "#c62828" : "#2e7d32" }}>{fmtCurrency(grandTotal)}</div>
						<div className="projectViewerKey">Contract Amount</div>
						<div className="projectViewerValue">{contractAmount != null ? fmtCurrency(contractAmount) : notApplicable}</div>
					</div>
					{budgetedOverContract && (
						<div className="projectViewerBudgetWarning danger" style={{ marginTop: 10 }}>
							<ErrorOutlineIcon style={{ fontSize: 18 }} />
							<div>Budgeted total exceeds the contract amount by {fmtCurrency(grandTotal - contractAmount)}. An amendment would be required.</div>
						</div>
					)}
					{saveError && (
						<div className="projectViewerBudgetWarning danger" style={{ marginTop: 10 }}>
							<ErrorOutlineIcon style={{ fontSize: 18 }} />
							<div>{saveError}</div>
						</div>
					)}
				</DialogContent>
				<DialogActions>
					<Button color="inherit" disabled={saving} onClick={() => setSaveReviewOpen(false)}>Keep Editing</Button>
					<Button variant="contained" disabled={saving} onClick={onConfirmSave}>
						{saving ? (bqeBudgetId ? "Saving…" : "Creating…") : (bqeBudgetId ? "Save Budget" : "Create Budget")}
					</Button>
				</DialogActions>
			</Dialog>

			{toast && (
				<div className="projectViewerBudgetToast">
					<span>{toast.text}</span>
					{toast.canUndo && (
						<button type="button" onClick={() => { dispatch({ type: "UNDO" }); setToast(null); }}>Undo</button>
					)}
				</div>
			)}
		</div>
	);
};

export default BudgetEditorTab;
